// Chiffre 3D Marquoir, moteur v3 (docs/CORRECTIONS-V2.md §2, sur la base de docs/REFONTE.md §3) : deux initiales
// en métal poli sur deux plans, bélière et chaîne forçat, studio procédural contrasté, caméra pilotée telle quelle
// par le défilement. Mêmes contours et même disposition que le SVG (lireContours, disposer) : construction du vrai
// bijou, seconde lettre en diagonale en bas à droite de la première, bélière tube sur le sommet de la première.
//
// creerChiffre3D(conteneur, { glyphesUrl, i1 = 'A', i2 = 'L', finition = 'doree', interactif = true,
//   cotes = { hauteur, largeur }, plein = false, chaine = true, progression = 0, qualite = 'auto', debord = 1 })
// → Promise de l'objet ci-dessous. Rejette si WebGL 2 est indisponible : l'appelant garde le dessin SVG.
//
// API (signatures v2 inchangées ; positions en px CSS relatifs au conteneur de la toile) :
// - setLettres(i1, i2, { animer = true })  la seconde lettre se pose (8 px, 300 ms), puis les cotes visibles se retracent.
// - setFinition('doree' | 'argentee')      fondu de 300 ms.
// - setProgression(p, { immediat = true }) p de 0 à 1, appliqué tel quel. Clés CLES = [0, 0.06, 0.30, 0.66, 0.84, 1] :
//     0 → 0,06 repos (vue 3/4 dans setCadre) ; 0,06 → 0,30 travelling vers le croisement ; 0,30 → 0,66 macro tenue
//     (légère poussée, panoramique, tangage montant) ; 0,66 → 0,84 recul ; 0,84 → 1 vue de face (dans setCadreFace).
// - setCadre({ x, y, w, h, plafond })      cadre de repos (p = 0). Appelable à chaque image, sans réallocation.
//     plafond (facultatif, px) : ligne au-dessus de laquelle la chaîne ne passe pas au repos (le bas d'un titre placé
//     au-dessus du pendant, en mobile) ; elle y est coupée net, comme glissée sous le texte, sans fondu ; la coupe se
//     lève pendant le travelling (p 0,06 → 0,16) et ne joue jamais en macro ni de face. Sans plafond, la chaîne monte
//     jusqu'au bord haut de la toile.
// - setCadreFace({ x, y, w, h } | null)    v3. Cadre de la vue de face (p ≥ 0,84), interpolé pendant le recul ;
//                                          null : même cadre que setCadre. Appelable à chaque image.
// - setMacro([{ cible: { x, y }, reserve: { x, y, w, h } }, …] | null)   v3. Pour chaque candidat, la macro la plus
//     serrée (au moins ×2 par rapport au repos, ×3 visé) où pendant, bélière et chaîne restent hors de `reserve`
//     agrandie de 16 px, le croisement étant posé sur `cible`. Retient le candidat de la macro la plus forte et émet
//     « mq:chiffre-macro » (bubbles, detail { indice, zoom }) chaque fois que ce choix change (lettres, taille,
//     candidats). Calcul sur le processeur (projection des contours), hors geste, pendant le temps libre.
//     null ou [] : macro par défaut (croisement centré dans la scène).
//     Plusieurs candidats peuvent partager la même réserve avec des cibles différentes : le moteur garde la meilleure
//     (sans recouvrement, ≥ ×2, la plus proche de ×3, puis le moins de papier vide contre la réserve). Repères testés
//     (docs/qa/v3-B/macro-paires.mjs, 12 paires) : desktop, colonne x 120 → 480, cibles x = 62, 72, 82 et 90 % de la
//     largeur à y = 55 % ; mobile, bande basse (y 62 → 92 %), cibles y = 8, 16, 24, 32, 40 et 48 %, et bande haute
//     (y 8 → 36 %), cibles y = 62 et 70 % (la chaîne monte toujours : une bande haute pleine largeur est rarement libre).
//     La réserve la plus juste (la boîte de l'annotation et le départ du filet, pas toute la colonne) donne les macros
//     les plus fortes : avec une boîte de 360 × 240 px en desktop, AL, JM, OO et QR passent à ×3, autour de la boîte.
// - afficherCotes(bool), tracerCotes(t)    tracé partiel de 0 à 1 : verticale, puis horizontale, puis valeurs en fondu.
// - pointEcran('croisement' | 'haut' | 'centre')  { x, y } ou null. « croisement » : moitié haute du recouvrement des
//     deux lettres le plus proche de la bélière (cible de la macro et du filet de rappel).
// - surImage(cb)                           cb({ progression, macro, largeur, hauteur }) après chaque rendu ; renvoie
//                                          la fonction de désabonnement.
// - Rotation à la main (option rotation = true) : glisser sur le pendant le fait tourner (élan au lâcher, double-clic :
//   retour de face), au repos et de face seulement ; astuce (texte) : petite indication, effacée au premier geste
//   (fiche seulement). Événement « mq:chiffre-rotation » au premier geste.
// - pause(), reprendre(), detruire()
// Événements sur le conteneur (bubbles) : « mq:chiffre-premiere-image » au premier rendu réel, avant ou après la
// résolution de la promesse ; « mq:chiffre-macro » (setMacro).
// Mode non plein (fiche) : pendant (lettres et bélière) centré dans le cadre, ou dans la toile sans setCadre, avec la
// place des cotes de chaque côté ; la chaîne sort par le bord haut, rognée par le conteneur, sans aucun fondu.
// Fluidité : préparation en petites étapes, au calme seulement (ni molette, ni défilement, ni toucher depuis 250 ms) ;
// toile allouée à sa taille finale et chauffée avant la première image ; DPR fixe : aucune réallocation de la toile
// pendant un geste ni à sa fin (une baisse de qualité mesurée ne s'applique qu'après 600 ms de calme).
// Contrôle : conteneur.mqChiffre3D = { api, qa } (non énumérable), utilisé par les scripts de docs/qa/v3-B :
//   qa.etapes (coût de chaque étape de préparation et calme à son départ), qa.etat() (toile, DPR, réallocations,
//   macro retenue, candidats…), qa.zoneFut(n) (zone de mesure des reflets), qa.masque(rect, p) (pixels de silhouette
//   dans rect, par un rendu masqué), qa.resoudre() (calcul immédiat de setMacro).

import * as THREE from 'three';
import { chargerGlyphes, lireContours, disposer, BELIERE, largeurBeliere, contourBeliere } from 'mq/chiffre-svg';

const { degToRad, clamp, lerp, smoothstep } = THREE.MathUtils;

// --- Géométrie (capitale = 1 unité) ---
const EPAISSEUR = 0.06; // ≈ 6 % de la capitale
const BISEAU = 0.0065; // biseau poli arrondi ≈ 0,6 % de la capitale, en largeur comme en profondeur
const BISEAU_SEG = 6;
const COURBE_SEG = 24;
const ECART = 0.0025; // jour entre les deux plans (aucun trait partagé, pas de scintillement)
const ANGLE_COIN = degToRad(24); // au-delà, l'arête du flanc reste vive
const MITRE_MAX = 2;
const ANNEAUX = 2 * (BISEAU_SEG + 1);
// Les deux lettres sont une seule pièce, comme sur le vrai bijou : elles s'interpénètrent dans le même plan. La seconde
// ne dépasse la première que d'un léger relief (RELIEF), assez pour lire l'ordre et éviter tout scintillement.
const RELIEF = 0.008;
const Z1 = -RELIEF / 2; // plan de la première lettre
const Z2 = RELIEF / 2; // plan de la seconde, en très léger relief
const Z_FOND = Z1 - EPAISSEUR / 2 - 0.05; // papier, derrière le pendentif

// --- Bélière tube (forme commune au SVG : BELIERE de chiffre-svg.js) et chaîne forçat, même matière ---
const BELIERE_TOUR = 64; // segments autour de la section
const BELIERE_CARRE = 2.2; // exposant de la section (superellipse) : tube rond, flancs à peine tendus
const BELIERE_TRAIT = 0.008; // demi-épaisseur de sa silhouette (setMacro)
const BELIERE_MAILLE = 0.75; // maille de la bélière, cachée dans le haut du tube (fraction de la hauteur du dôme)
const MAILLE = { R: 0.021, r: 0.0058, s: 0.03 }; // rayon des boucles, rayon du fil, partie droite
const PAS = MAILLE.s + 2 * MAILLE.R - 2 * MAILLE.r; // d'une maille à la suivante
const BRIN = 3.6; // longueur de chaque brin : la chaîne sort toujours de la toile par le haut, jamais estompée
const BRIN_ANGLE = { depart: degToRad(40), fin: degToRad(13), longueur: 0.45 };
const BRIN_RECUL = 0.06;
const DEMI_MAILLE = MAILLE.R + MAILLE.s / 2 + MAILLE.r; // demi-encombrement d'une maille (silhouette)

// --- Caméra et chorégraphie (CORRECTIONS-V2 §1), p reçu tel quel ---
const FOV = 24;
const CLES = [0, 0.06, 0.30, 0.66, 0.84, 1]; // repos, début du travelling, macro, fin de macro, face, fin
const LACETS = [-26, -26, -16, -7, 0, 0].map(degToRad);
// tangage montant pendant la macro : la caméra passe sous le croisement, les deux plans glissent l'un sur l'autre
const TANGAGES = [7, 7, 3, -3, 0, 0].map(degToRad);
// l'environnement tourne un peu avec p : une bande claire glisse sur les faces dès le premier geste
const ROTATIONS_ENV = [0, 5, -4, 4, -2, 2].map(degToRad);
const MACRO = { mini: 2, vise: 3, plancher: 1.4, poussee: 1.04, marge: 16, remplissage: 0.6 };
const MACRO_DEFAUT = { remplissage: 0.7, mini: 3 }; // sans setMacro : le croisement remplit ≈ 70 % de la hauteur
// balancement de repos : le lacet fait glisser les reflets sur les faces ; un tangage faible garde la bande sombre
// dans le cône de chaque face (±0,5° de tangage = ±1° de site reflété)
const BALANCEMENT = { lacet: degToRad(2.5), tangage: degToRad(0.5), periodeL: 7.3, periodeT: 11.1 };
const POINTEUR = { lacet: degToRad(3), tangage: degToRad(2) };
// Rotation à la main : lacet libre (rad par px glissé), tangage borné (souris seulement), élan amorti au lâcher,
// retour de face au double-clic. rayon : zone de prise autour du centre du pendant, en hauteurs de capitale.
const ROTATION = { parPx: 0.008, tangageParPx: 0.004, tangage: degToRad(20), rayon: 1.3, amorti: 0.25, retour: 0.18 };

// --- Mouvement ---
const DUREE_POSE = 300;
const DUREE_TRACE = 240;
const DUREE_FINITION = 300;
const GLISSEMENT_PX = 8;

// --- Fluidité : rien de coûteux pendant un geste ---
const CALME_MS = 250; // préparation et calculs seulement après 250 ms sans molette, défilement ni toucher
const CALME_QUALITE_MS = 600; // une baisse de définition mesurée ne s'applique qu'après 600 ms de calme
const PIXELS_MAX = 4.5e6;
const LENT_S = 0.03; // moyenne d'images au-delà de 30 ms…
const LENT_IMAGES = 90; // …pendant au moins 90 images d'animation continue : qualité automatique réduite

// --- Matière : F0 linéaires, sans couleur criarde ---
const FINITIONS = {
  doree: { f0: [0.95, 0.74, 0.4], rugosite: 0.15 },
  argentee: { f0: [0.62, 0.62, 0.64], rugosite: 0.13 },
};
const EXPOSITION = 1;
// Sous 0,21, le PMREM de Three.js lit un niveau environ deux fois plus flou qu'un vrai lobe GGX de même
// rugosité : les faces planes perdaient leurs bandes (aspect satiné). La rugosité physique reste celle du
// cahier (0,14-0,2) ; seule la lecture de l'environnement est affinée.
const NETTETE_REFLETS = 0.5;
const CHUNK_REFLETS = 'envMapRotation * reflectVec, roughness )';
const OPACITE_PORTEE = 0.42;
const OPACITE_OMBRE = 0.12; // ≤ 0,14
const OMBRE_DECALAGE = [0.022, -0.03]; // lumière en haut à gauche : ombre en bas à droite

// --- Studio procédural : pièce sombre et neutre, boîtes douces en dégradé, filets fins et vifs, passé par PMREM.
// a : azimut compté de +Z (derrière la caméra de face) vers +X ; s : site au-dessus de l'horizon ; l, h : taille
// angulaire ; i : intensité ; d : douceur des bords latéraux ; b, t : douceur du bas et du haut (fraction de la
// hauteur) ; gx, gy : dégradé linéaire le long de la largeur, de la hauteur ; r : roulis ; tous en degrés.
// Une face plane ne reflète qu'un petit cône. Le centre du pendant y lit (26°, −7°) au repos et (−2°, 0°) de face ;
// le fût d'une lettre couvre 4° de site en mobile, 7° en desktop et sur la fiche, et le balancement le déplace de ±1°.
// Chaque cône reçoit donc, en site, une boîte claire, une transition raide de 2° centrée sur lui (−6,6° au repos,
// 0° de face), une bande sombre, puis un réflecteur moyen : clair en haut, sombre au milieu, reflet doux en bas, à
// tous les instants et dans toutes les mises en page (docs/qa/v3-B : écart P5–P95 ≥ 92 sur le fût, or et argent).
// Les filets nets sont en diagonale, dans les parties claires ou basses : ils ne couvrent jamais un fût entier.
// L'environnement suit le pendant à l'écran dans les vues cadrées et l'écran en macro (voir rendre()).
const STUDIO = {
  fond: 0.13, // pièce relevée : flancs et parties sombres de l'argent restent gris, jamais noirs
  sol: 0.15,
  plafond: 0.16,
  panneaux: [
    { a: 0, s: 80, l: 120, h: 60, i: 1.0, d: 0.6 }, // boîte douce au-dessus : dessus des biseaux et des empattements
    // repos (vue 3/4) : bande claire de −7,7° à +4° (transition raide en bas), bande sombre, réflecteur bas ; au-dessus,
    // la pièce sombre : la grande zone de la macro voit des bandes franches, pas un aplat clair
    { a: 27, s: -1.85, l: 26, h: 11.7, i: 1.15, d: 0.25, b: 0.19, t: 0.17 },
    { a: 27, s: -15.5, l: 26, h: 10, i: 0.5, d: 0.25, b: 0.3, t: 0.15 },
    // face : bande claire de −1,2° à +9°, bande sombre, réflecteur bas
    { a: 0, s: 3.9, l: 28, h: 10.2, i: 1.15, d: 0.25, b: 0.235, t: 0.2 },
    { a: 0, s: -8, l: 28, h: 10, i: 0.5, d: 0.25, b: 0.3, t: 0.15 },
    // filets diagonaux : une ligne claire franche dans le haut et le bas de chaque cône
    { a: 24, s: -1.5, l: 1, h: 10, i: 6, d: 0.3, r: 35 },
    { a: 30, s: -13.5, l: 1, h: 8, i: 4, d: 0.3, r: -30 },
    { a: -4, s: 4.5, l: 1, h: 10, i: 6, d: 0.3, r: -35 },
    { a: 3, s: -5.5, l: 1, h: 8, i: 4, d: 0.3, r: 30 },
    // filets hauts de la macro, hors des cônes de repos (azimut 12-36°) et de face (−8-9°) : pendant la macro tenue,
    // le cône balaie de 20° à 3° d'azimut et ces filets traversent les faces en bandes nettes
    { a: 10.5, s: 2, l: 1.6, h: 50, i: 7, d: 0.3, r: 4 },
    { a: -12, s: 0, l: 1.6, h: 50, i: 6, d: 0.3, r: -4 },
    { a: 37.5, s: 2, l: 2.5, h: 50, i: 5, d: 0.35, r: -5 },
    // bandes plus loin du centre : éclats des biseaux, bandes de la macro
    { a: -24, s: 4, l: 5, h: 72, i: 3.2, d: 0.35 },
    { a: 48, s: 2, l: 5, h: 72, i: 3, d: 0.35 },
    { a: 41, s: 0, l: 1.4, h: 70, i: 10, d: 0.3, r: 6 },
    { a: 12, s: -24, l: 80, h: 4, i: 2.2, d: 0.4 }, // bande horizontale basse (biseaux du bas)
    // derrière l'objet : flancs (vues 3/4 et macro) et dessus des empattements
    { a: -160, s: 2, l: 40, h: 46, i: 0.8, d: 0.6 },
    { a: -146, s: 4, l: 3, h: 50, i: 5, d: 0.3 },
    { a: 155, s: 10, l: 36, h: 34, i: 0.8, d: 0.55 },
    { a: 172, s: -6, l: 2.5, h: 50, i: 4, d: 0.3 },
  ],
};

const STYLE_VALEUR = "font-family:var(--font-mesure,'IBM Plex Mono',ui-monospace,Menlo,Consolas,monospace);font-size:13px;fill:var(--encre,#1a1f2b)";
const NS = 'http://www.w3.org/2000/svg';
const ENCRE = '#1a1f2b';

/** Courbe de Bézier CSS (x1, y1, x2, y2) → fonction de temps. */
function courbeBezier(x1, y1, x2, y2) {
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  const X = (t) => ((ax * t + bx) * t + cx) * t;
  const Y = (t) => ((ay * t + by) * t + cy) * t;
  const dX = (t) => (3 * ax * t + 2 * bx) * t + cx;
  return (x) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 8; i += 1) {
      const e = X(t) - x;
      if (Math.abs(e) < 1e-6) return Y(t);
      const d = dX(t);
      if (Math.abs(d) < 1e-6) break;
      t -= e / d;
    }
    let a = 0;
    let b = 1;
    t = x;
    for (let i = 0; i < 30; i += 1) {
      const v = X(t);
      if (Math.abs(v - x) < 1e-6) break;
      if (v < x) a = t; else b = t;
      t = (a + b) / 2;
    }
    return Y(t);
  };
}

const POSE = courbeBezier(0.2, 0, 0, 1); // easing de marque « pose »

function normaliser(lettre) {
  return String(lettre ?? '').trim().toUpperCase();
}

// --- Gestes : molette, défilement et toucher, suivis une fois pour toutes les instances ---
let dernierGeste = -Infinity;
let gestesSuivis = false;
function noterGeste() {
  dernierGeste = performance.now();
}
function suivreGestes() {
  if (gestesSuivis) return;
  gestesSuivis = true;
  const o = { passive: true, capture: true };
  for (const type of ['wheel', 'scroll', 'touchstart', 'touchmove']) window.addEventListener(type, noterGeste, o);
}
const calmeDepuis = () => performance.now() - dernierGeste;

/**
 * Attend un temps libre du navigateur ET le calme (aucun geste depuis `calme` ms). Pas de délai court forcé :
 * une étape de préparation ou de calcul ne tombe jamais pendant un geste.
 */
function souffler(calme = CALME_MS) {
  return new Promise((ok) => {
    const essayer = () => {
      const reste = calme - calmeDepuis();
      if (reste > 0) {
        window.setTimeout(essayer, reste + 8);
        return;
      }
      const suite = () => {
        if (calmeDepuis() < calme) essayer();
        else ok();
      };
      if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(suite, { timeout: 2000 });
      else window.setTimeout(suite, 32);
    };
    essayer();
  });
}

// --- Interpolation monotone (Fritsch-Butland) : aucun dépassement entre deux clés ---
function pentes(xs, ys, o, m) {
  const n = xs.length;
  for (let i = 0; i < n; i += 1) {
    if (i === 0) m[o] = (ys[o + 1] - ys[o]) / (xs[1] - xs[0]);
    else if (i === n - 1) m[o + i] = (ys[o + i] - ys[o + i - 1]) / (xs[i] - xs[i - 1]);
    else {
      const h0 = xs[i] - xs[i - 1];
      const h1 = xs[i + 1] - xs[i];
      const d0 = (ys[o + i] - ys[o + i - 1]) / h0;
      const d1 = (ys[o + i + 1] - ys[o + i]) / h1;
      m[o + i] = d0 * d1 <= 0 ? 0 : (3 * (h0 + h1)) / ((2 * h1 + h0) / d0 + (h1 + 2 * h0) / d1);
    }
  }
}

function hermite(xs, ys, o, m, x) {
  let i = 0;
  while (i < xs.length - 2 && x > xs[i + 1]) i += 1;
  const h = xs[i + 1] - xs[i];
  const t = clamp((x - xs[i]) / h, 0, 1);
  const t2 = t * t;
  const t3 = t2 * t;
  return (2 * t3 - 3 * t2 + 1) * ys[o + i] + (t3 - 2 * t2 + t) * h * m[o + i] + (-2 * t3 + 3 * t2) * ys[o + i + 1] + (t3 - t2) * h * m[o + i + 1];
}

// --- Contours des lettres ---

/** Échantillonne un contour (commandes M L Q C Z) : points 2D, jonctions entre commandes, aire signée. */
function echantillonner(commandes, echelle, segments = COURBE_SEG) {
  const xs = [];
  const ys = [];
  const jonction = [];
  const pousser = (x, y, j) => {
    const n = xs.length;
    if (n && Math.abs(xs[n - 1] - x) < 1e-7 && Math.abs(ys[n - 1] - y) < 1e-7) {
      if (j) jonction[n - 1] = 1;
      return;
    }
    xs.push(x);
    ys.push(y);
    jonction.push(j ? 1 : 0);
  };
  let cx = 0;
  let cy = 0;
  for (const [t, ...v] of commandes) {
    const p = v.map((x) => x * echelle);
    if (t === 'M' || t === 'L') {
      cx = p[0];
      cy = p[1];
      pousser(cx, cy, true);
    } else if (t === 'Q') {
      for (let k = 1; k <= segments; k += 1) {
        const u = k / segments;
        const w = 1 - u;
        pousser(w * w * cx + 2 * w * u * p[0] + u * u * p[2], w * w * cy + 2 * w * u * p[1] + u * u * p[3], k === segments);
      }
      cx = p[2];
      cy = p[3];
    } else if (t === 'C') {
      for (let k = 1; k <= segments; k += 1) {
        const u = k / segments;
        const w = 1 - u;
        const a = w * w * w;
        const b = 3 * w * w * u;
        const c = 3 * w * u * u;
        const d = u * u * u;
        pousser(a * cx + b * p[0] + c * p[2] + d * p[4], a * cy + b * p[1] + c * p[3] + d * p[5], k === segments);
      }
      cx = p[4];
      cy = p[5];
    }
  }
  let n = xs.length;
  if (n > 1 && Math.abs(xs[0] - xs[n - 1]) < 1e-7 && Math.abs(ys[0] - ys[n - 1]) < 1e-7) {
    xs.pop();
    ys.pop();
    jonction.pop();
    jonction[0] = 1;
    n -= 1;
  }
  if (n < 3) return null;
  let aire = 0;
  for (let i = 0, j = n - 1; i < n; j = i, i += 1) aire += xs[j] * ys[i] - xs[i] * ys[j];
  return { n, x: Float64Array.from(xs), y: Float64Array.from(ys), jonction: Uint8Array.from(jonction), aire: aire / 2 };
}

function inverser(c) {
  c.x.reverse();
  c.y.reverse();
  c.jonction.reverse();
  c.aire = -c.aire;
}

function dansContour(px, py, c) {
  let dedans = false;
  for (let i = 0, j = c.n - 1; i < c.n; j = i, i += 1) {
    if ((c.y[i] > py) !== (c.y[j] > py) && px < ((c.x[j] - c.x[i]) * (py - c.y[i])) / (c.y[j] - c.y[i]) + c.x[i]) dedans = !dedans;
  }
  return dedans;
}

/**
 * Colonnes de sommets d'un contour : une par point lisse, deux aux coins (arête vive, normales de chaque côté).
 * Contours extérieurs dans le sens direct, contre-formes en sens inverse : la normale sortante est toujours
 * à droite du sens de parcours.
 */
function preparerColonnes(c) {
  const { n, x, y } = c;
  const mx = new Float64Array(n);
  const my = new Float64Array(n);
  const coin = new Uint8Array(n);
  const nor = new Float64Array(n * 6); // normale du segment précédent, du suivant, moyenne
  for (let i = 0; i < n; i += 1) {
    const a = (i + n - 1) % n;
    const b = (i + 1) % n;
    let ex = x[i] - x[a];
    let ey = y[i] - y[a];
    let l = Math.hypot(ex, ey) || 1;
    ex /= l;
    ey /= l;
    let fx = x[b] - x[i];
    let fy = y[b] - y[i];
    l = Math.hypot(fx, fy) || 1;
    fx /= l;
    fy /= l;
    let ax = ey + fy;
    let ay = -ex - fx;
    l = Math.hypot(ax, ay);
    if (l < 1e-6) {
      ax = fy;
      ay = -fx;
    } else {
      ax /= l;
      ay /= l;
    }
    const k = Math.min(MITRE_MAX, 1 / Math.max(ax * fy - ay * fx, 1e-3));
    mx[i] = ax * k;
    my[i] = ay * k;
    coin[i] = c.jonction[i] && Math.acos(clamp(ex * fx + ey * fy, -1, 1)) > ANGLE_COIN ? 1 : 0;
    nor.set([ey, -ex, fy, -fx, ax, ay], i * 6);
  }
  let nCol = 0;
  for (let i = 0; i < n; i += 1) nCol += coin[i] ? 2 : 1;
  const colP = new Int32Array(nCol);
  const colN = new Float64Array(nCol * 2);
  const second = new Uint8Array(nCol);
  const premiere = new Int32Array(n);
  let j = 0;
  for (let i = 0; i < n; i += 1) {
    premiere[i] = j;
    if (coin[i]) {
      colP[j] = i;
      colN[2 * j] = nor[i * 6];
      colN[2 * j + 1] = nor[i * 6 + 1];
      j += 1;
      colP[j] = i;
      colN[2 * j] = nor[i * 6 + 2];
      colN[2 * j + 1] = nor[i * 6 + 3];
      second[j] = 1;
      j += 1;
    } else {
      colP[j] = i;
      colN[2 * j] = nor[i * 6 + 4];
      colN[2 * j + 1] = nor[i * 6 + 5];
      j += 1;
    }
  }
  let nQuads = 0;
  for (let k = 0; k < nCol; k += 1) if (!second[(k + 1) % nCol]) nQuads += 1;
  Object.assign(c, { mx, my, colP, colN, second, premiere, nCol, nQuads });
}

/**
 * Volume d'une lettre : faces planes, biseau arrondi poli à normales analytiques (quart de cercle,
 * 6 segments), flancs lisses sur les courbes et vifs aux coins. Centrée en z, capitale = 1.
 */
function construireLettre(glyphe, echelle) {
  const contours = [];
  for (const commandes of lireContours(glyphe.contours)) {
    const c = echantillonner(commandes, echelle);
    if (c && Math.abs(c.aire) > 1e-9) contours.push(c);
  }
  const geo = new THREE.BufferGeometry();
  if (!contours.length) return geo;
  let grand = contours[0];
  for (const c of contours) if (Math.abs(c.aire) > Math.abs(grand.aire)) grand = c;
  const signe = Math.sign(grand.aire);
  const formes = [];
  const trous = [];
  for (const c of contours) {
    if (Math.sign(c.aire) === signe) {
      if (c.aire < 0) inverser(c);
      formes.push({ ext: c, trous: [] });
    } else {
      if (c.aire > 0) inverser(c);
      trous.push(c);
    }
  }
  for (const t of trous) {
    let hote = null;
    for (const f of formes) {
      if (dansContour(t.x[0], t.y[0], f.ext) && (!hote || Math.abs(f.ext.aire) < Math.abs(hote.ext.aire))) hote = f;
    }
    (hote || formes[0]).trous.push(t);
  }
  const tous = [];
  for (const f of formes) tous.push(f.ext, ...f.trous);
  let nSommets = 0;
  let nIndices = 0;
  for (const c of tous) {
    preparerColonnes(c);
    c.base = nSommets;
    nSommets += c.nCol * ANNEAUX;
    nIndices += c.nQuads * (ANNEAUX - 1) * 6;
  }
  // faces avant et arrière : triangulation du contour exact, appliquée au contour rentré du biseau
  const faces = formes.map((f) => {
    const liste = [f.ext, ...f.trous];
    const v2 = liste.map((c) => Array.from({ length: c.n }, (_, i) => new THREE.Vector2(c.x[i], c.y[i])));
    const tri = THREE.ShapeUtils.triangulateShape(v2[0], v2.slice(1));
    const refs = [];
    for (const c of liste) for (let i = 0; i < c.n; i += 1) refs.push(c, i);
    nIndices += tri.length * 6;
    return { tri, refs };
  });
  const pos = new Float32Array(nSommets * 3);
  const nor = new Float32Array(nSommets * 3);
  const index = nSommets > 65535 ? new Uint32Array(nIndices) : new Uint16Array(nIndices);
  const sinT = new Float64Array(BISEAU_SEG + 1);
  const cosT = new Float64Array(BISEAU_SEG + 1);
  for (let k = 0; k <= BISEAU_SEG; k += 1) {
    sinT[k] = Math.sin((k / BISEAU_SEG) * (Math.PI / 2));
    cosT[k] = Math.cos((k / BISEAU_SEG) * (Math.PI / 2));
  }
  let w = 0;
  for (const c of tous) {
    for (let j = 0; j < c.nCol; j += 1) {
      const i = c.colP[j];
      const nx = c.colN[2 * j];
      const ny = c.colN[2 * j + 1];
      for (let r = 0; r < ANNEAUX; r += 1) {
        const avant = r <= BISEAU_SEG;
        const k = avant ? r : ANNEAUX - 1 - r;
        const s = sinT[k];
        const co = cosT[k];
        const decalage = -BISEAU * (1 - s);
        const v = 3 * (c.base + j * ANNEAUX + r);
        pos[v] = c.x[i] + c.mx[i] * decalage;
        pos[v + 1] = c.y[i] + c.my[i] * decalage;
        pos[v + 2] = (EPAISSEUR / 2 - BISEAU * (1 - co)) * (avant ? 1 : -1);
        nor[v] = nx * s;
        nor[v + 1] = ny * s;
        nor[v + 2] = avant ? co : -co;
      }
    }
    for (let j = 0; j < c.nCol; j += 1) {
      const s = (j + 1) % c.nCol;
      if (c.second[s]) continue;
      for (let r = 0; r < ANNEAUX - 1; r += 1) {
        const a = c.base + j * ANNEAUX + r;
        const b = c.base + s * ANNEAUX + r;
        index[w++] = a;
        index[w++] = a + 1;
        index[w++] = b + 1;
        index[w++] = a;
        index[w++] = b + 1;
        index[w++] = b;
      }
    }
  }
  for (const { tri, refs } of faces) {
    const sommet = (k, arriere) => {
      const c = refs[2 * k];
      return c.base + c.premiere[refs[2 * k + 1]] * ANNEAUX + (arriere ? ANNEAUX - 1 : 0);
    };
    const px = (k) => refs[2 * k].x[refs[2 * k + 1]];
    const py = (k) => refs[2 * k].y[refs[2 * k + 1]];
    for (const [a, b, c] of tri) {
      const direct = (px(b) - px(a)) * (py(c) - py(a)) - (py(b) - py(a)) * (px(c) - px(a)) > 0;
      const [u, v] = direct ? [b, c] : [c, b];
      index[w++] = sommet(a, false);
      index[w++] = sommet(u, false);
      index[w++] = sommet(v, false);
      index[w++] = sommet(a, true);
      index[w++] = sommet(v, true);
      index[w++] = sommet(u, true);
    }
  }
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  geo.setIndex(new THREE.BufferAttribute(index.subarray(0, w), 1));
  geo.computeBoundingBox();
  geo.computeBoundingSphere();
  return geo;
}

/** Maille forçat : tore en « stade » (deux droites, deux demi-cercles), grand axe X, ouverture face à Z. */
function geometrieMaille({ R, r, s }, segArc = 10, segFil = 10) {
  const ligne = [];
  for (let k = 0; k < 2; k += 1) ligne.push([-s / 2 + (s * k) / 2, R, 0, 1]);
  for (let k = 0; k < segArc; k += 1) {
    const t = Math.PI / 2 - (Math.PI * k) / segArc;
    ligne.push([s / 2 + R * Math.cos(t), R * Math.sin(t), Math.cos(t), Math.sin(t)]);
  }
  for (let k = 0; k < 2; k += 1) ligne.push([s / 2 - (s * k) / 2, -R, 0, -1]);
  for (let k = 0; k < segArc; k += 1) {
    const t = -Math.PI / 2 - (Math.PI * k) / segArc;
    ligne.push([-s / 2 + R * Math.cos(t), R * Math.sin(t), Math.cos(t), Math.sin(t)]);
  }
  const nU = ligne.length;
  const pos = new Float32Array(nU * segFil * 3);
  const nor = new Float32Array(nU * segFil * 3);
  const index = new Uint16Array(nU * segFil * 6);
  let w = 0;
  for (let i = 0; i < nU; i += 1) {
    const [cx, cy, nx, ny] = ligne[i];
    for (let j = 0; j < segFil; j += 1) {
      const v = (j / segFil) * Math.PI * 2;
      const o = 3 * (i * segFil + j);
      nor[o] = Math.cos(v) * nx;
      nor[o + 1] = Math.cos(v) * ny;
      nor[o + 2] = Math.sin(v);
      pos[o] = cx + r * nor[o];
      pos[o + 1] = cy + r * nor[o + 1];
      pos[o + 2] = r * nor[o + 2];
      const a = i * segFil + j;
      const b = ((i + 1) % nU) * segFil + j;
      const c = ((i + 1) % nU) * segFil + ((j + 1) % segFil);
      const d = i * segFil + ((j + 1) % segFil);
      index[w++] = a;
      index[w++] = d;
      index[w++] = b;
      index[w++] = b;
      index[w++] = d;
      index[w++] = c;
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  geo.setIndex(new THREE.BufferAttribute(index, 1));
  geo.computeBoundingSphere();
  return geo;
}

/**
 * Bélière tube, comme sur le vrai bijou : verticale, légèrement conique (plus large en haut), coiffée d'un dôme,
 * section ronde un peu tendue ; elle enserre l'épaisseur du pendentif. Pied au centre en (0, 0, 0), y vers le haut,
 * profondeur en z. Les sommets du pied et du dôme sont uniques : normales lisses, sans couture.
 */
function geometrieBeliere() {
  const yd = BELIERE.hauteur - BELIERE.dome;
  const anneaux = [];
  // pied : petit arrondi, puis le cône droit
  anneaux.push([0, 0.86], [0.004, 0.97], [0.01, 1]);
  for (let i = 1; i <= 6; i += 1) anneaux.push([0.01 + ((yd - 0.01) * i) / 6, 1]);
  for (let i = 1; i < 16; i += 1) anneaux.push([yd + BELIERE.dome * Math.sin((i / 16) * (Math.PI / 2)), 1]);
  const nA = anneaux.length;
  const N = BELIERE_TOUR;
  const pos = new Float32Array((nA * N + 2) * 3);
  const e = 2 / BELIERE_CARRE;
  let o = 0;
  for (const [y, f] of anneaux) {
    const a = largeurBeliere(y) * f;
    const b = largeurBeliere(y, BELIERE.profondeurBas / 2, BELIERE.profondeur / 2) * f;
    for (let k = 0; k < N; k += 1) {
      const t = (k / N) * Math.PI * 2;
      const c = Math.cos(t);
      const sn = Math.sin(t);
      pos[o++] = a * Math.sign(c) * Math.abs(c) ** e;
      pos[o++] = y;
      pos[o++] = b * Math.sign(sn) * Math.abs(sn) ** e;
    }
  }
  const bas = nA * N;
  const haut = bas + 1;
  pos.set([0, 0, 0, 0, BELIERE.hauteur, 0], bas * 3);
  const index = [];
  for (let r = 0; r + 1 < nA; r += 1) {
    for (let k = 0; k < N; k += 1) {
      const a = r * N + k;
      const b = r * N + ((k + 1) % N);
      const c = a + N;
      const d = b + N;
      index.push(a, c, b, b, c, d);
    }
  }
  for (let k = 0; k < N; k += 1) {
    index.push(bas, k, (k + 1) % N);
    index.push(haut, (nA - 1) * N + ((k + 1) % N), (nA - 1) * N + k);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setIndex(index);
  geo.computeVertexNormals();
  geo.computeBoundingSphere();
  return geo;
}

/**
 * Chaîne : un V qui passe dans la bélière, deux brins en chaînette qui montent et s'éloignent un peu.
 * Renvoie, pour chaque brin, l'intervalle [début, fin[ de ses mailles (la maille 0 est celle de la bélière).
 */
function poserChaine(liens) {
  const m = new THREE.Matrix4();
  const X = new THREE.Vector3();
  const Y = new THREE.Vector3();
  const Zv = new THREE.Vector3();
  const Zw = new THREE.Vector3(0, 0, 1);
  const poser = (k, x, y, z, tx, ty, tz, plate) => {
    X.set(tx, ty, tz).normalize();
    if (plate) Y.crossVectors(Zw, X).normalize();
    else Y.copy(Zw).addScaledVector(X, -X.z).normalize();
    Zv.crossVectors(X, Y);
    m.makeBasis(X, Y, Zv).setPosition(x, y, z);
    liens.setMatrixAt(k, m);
  };
  const brins = [];
  let k = 0;
  poser(k++, 0, 0, 0, 1, 0, 0, true);
  for (const cote of [1, -1]) {
    const debut = k;
    let x = 0;
    let y = 0;
    let s = 0;
    let prochain = PAS;
    let rang = 1;
    const ds = 0.002;
    while (s < BRIN && k < liens.instanceMatrix.count) {
      const a = BRIN_ANGLE.fin + (BRIN_ANGLE.depart - BRIN_ANGLE.fin) * Math.exp(-s / BRIN_ANGLE.longueur);
      x += cote * Math.sin(a) * ds;
      y += Math.cos(a) * ds;
      s += ds;
      if (s >= prochain) {
        const z = -BRIN_RECUL * (1 - Math.exp(-s / 0.8));
        const tz = (-BRIN_RECUL / 0.8) * Math.exp(-s / 0.8);
        poser(k++, x, y, z, cote * Math.sin(a), Math.cos(a), tz, rang % 2 === 0);
        prochain += PAS;
        rang += 1;
      }
    }
    brins.push([debut, k]);
  }
  liens.count = k;
  liens.instanceMatrix.needsUpdate = true;
  liens.computeBoundingSphere();
  return brins;
}

function nombreMailles() {
  return 1 + 2 * Math.ceil(BRIN / PAS);
}

// --- Analyse 2D du chiffre (même géométrie que le SVG) ---

function chemin2D(glyphe, dx, dy = 0) {
  const p = new Path2D();
  for (const contour of lireContours(glyphe.contours)) {
    for (const [t, ...v] of contour) {
      if (t === 'M') p.moveTo(v[0] + dx, v[1] + dy);
      else if (t === 'L') p.lineTo(v[0] + dx, v[1] + dy);
      else if (t === 'Q') p.quadraticCurveTo(v[0] + dx, v[1] + dy, v[2] + dx, v[3] + dy);
      else if (t === 'C') p.bezierCurveTo(v[0] + dx, v[1] + dy, v[2] + dx, v[3] + dy, v[4] + dx, v[5] + dy);
      else if (t === 'Z') p.closePath();
    }
  }
  return p;
}

/**
 * Rasterise les deux lettres (capitale de 140 px : assez fin, et court au premier passage) et en tire :
 * - le croisement : parmi les zones où les deux lettres se recouvrent, celle qui est la plus proche de la bélière,
 *   et le centre de sa moitié haute (là où un jambage passe derrière l'autre lettre, pas le pied des lettres) ;
 * - le fût de chaque lettre (plus longue verticale de matière) : zone de mesure des reflets (contrôle).
 */
function analyserChiffre(dispo, glyphes) {
  const [x0, , x1, y1] = dispo.boite;
  const y0 = dispo.boite[1];
  const cap = glyphes.capHeight;
  const k = 140 / cap;
  const W = Math.ceil((x1 - x0) * k) + 4;
  const H = Math.ceil((y1 - y0) * k) + 4;
  const N = W * H;
  const toile = document.createElement('canvas');
  toile.width = W;
  toile.height = H;
  const ctx = toile.getContext('2d', { willReadFrequently: true });
  const lire = (glyphe, dx, dy) => {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, W, H);
    ctx.setTransform(k, 0, 0, -k, 2 - x0 * k, 2 + y1 * k);
    ctx.fill(chemin2D(glyphe, dx, dy));
    const donnees = ctx.getImageData(0, 0, W, H).data;
    const masque = new Uint8Array(N);
    for (let o = 0; o < N; o += 1) masque[o] = donnees[o * 4 + 3] > 127 ? 1 : 0;
    return masque;
  };
  const m1 = lire(dispo.lettres[0].glyphe, 0, 0);
  const m2 = lire(dispo.lettres[1].glyphe, dispo.lettres[1].x, dispo.lettres[1].y);
  const versX = (i) => x0 + (i - 2) / k;
  const versY = (j) => y1 - (j - 2) / k;

  // accroche de la bélière : sommet de la première lettre (disposer, commun au SVG)
  const iBest = Math.round((dispo.beliere[0] - x0) * k + 2);
  const jTop = Math.round((y1 - dispo.beliere[1]) * k + 2);

  // recouvrements : composantes connexes de l'intersection des deux lettres ; la plus proche de la bélière
  const marque = new Int32Array(N);
  const file = new Int32Array(N);
  const minimum = Math.max(12, Math.round(0.0015 * cap * cap * k * k));
  let choisie = null;
  let id = 0;
  for (let o0 = 0; o0 < N; o0 += 1) {
    if (!m1[o0] || !m2[o0] || marque[o0]) continue;
    id += 1;
    let tete = 0;
    let queue = 0;
    file[queue++] = o0;
    marque[o0] = id;
    let n = 0;
    let jMin = H;
    let jMax = -1;
    let iMin = W;
    let iMax = -1;
    let d2 = Infinity;
    while (tete < queue) {
      const o = file[tete++];
      const i = o % W;
      const j = (o - i) / W;
      n += 1;
      if (j < jMin) jMin = j;
      if (j > jMax) jMax = j;
      if (i < iMin) iMin = i;
      if (i > iMax) iMax = i;
      const dx = i - iBest;
      const dy = j - jTop;
      const d = dx * dx + dy * dy;
      if (d < d2) d2 = d;
      if (i > 0 && m1[o - 1] && m2[o - 1] && !marque[o - 1]) { marque[o - 1] = id; file[queue++] = o - 1; }
      if (i < W - 1 && m1[o + 1] && m2[o + 1] && !marque[o + 1]) { marque[o + 1] = id; file[queue++] = o + 1; }
      if (j > 0 && m1[o - W] && m2[o - W] && !marque[o - W]) { marque[o - W] = id; file[queue++] = o - W; }
      if (j < H - 1 && m1[o + W] && m2[o + W] && !marque[o + W]) { marque[o + W] = id; file[queue++] = o + W; }
    }
    if (n < minimum) continue;
    if (!choisie || d2 < choisie.d2 - 2 || (Math.abs(d2 - choisie.d2) <= 2 && n > choisie.n)) choisie = { id, n, jMin, jMax, iMin, iMax, d2 };
  }
  let croisement = dispo.croisement;
  let hauteur = 0.5;
  let largeur = 0.5;
  if (choisie) {
    const jMoitie = (choisie.jMin + choisie.jMax) / 2;
    let n = 0;
    let sx = 0;
    let sy = 0;
    for (let j = choisie.jMin; j <= jMoitie; j += 1) {
      for (let i = choisie.iMin; i <= choisie.iMax; i += 1) {
        if (marque[j * W + i] === choisie.id) {
          n += 1;
          sx += i;
          sy += j;
        }
      }
    }
    if (n) croisement = [versX(sx / n), versY(sy / n)];
    hauteur = (choisie.jMax - choisie.jMin) / k / cap;
    largeur = (choisie.iMax - choisie.iMin) / k / cap;
  }

  // fûts : plus longue verticale de matière de chaque lettre, au milieu du plus large groupe de colonnes
  const futs = [m1, m2].map((m) => {
    const longueur = new Int32Array(W);
    const depart = new Int32Array(W);
    let max = 0;
    for (let i = 0; i < W; i += 1) {
      let run = 0;
      for (let j = 0; j < H; j += 1) {
        if (m[j * W + i]) {
          run += 1;
          if (run > longueur[i]) {
            longueur[i] = run;
            depart[i] = j - run + 1;
          }
        } else run = 0;
      }
      if (longueur[i] > max) max = longueur[i];
    }
    if (!max) return null;
    let meilleurGroupe = null;
    for (let i = 0; i < W;) {
      if (longueur[i] < 0.9 * max) {
        i += 1;
        continue;
      }
      const a = i;
      while (i < W && longueur[i] >= 0.9 * max) i += 1;
      if (!meilleurGroupe || i - a > meilleurGroupe[1] - meilleurGroupe[0]) meilleurGroupe = [a, i];
    }
    const ic = Math.round((meilleurGroupe[0] + meilleurGroupe[1] - 1) / 2);
    return {
      x: versX(ic),
      demiLargeur: (meilleurGroupe[1] - meilleurGroupe[0]) / 2 / k,
      haut: versY(depart[ic]),
      bas: versY(depart[ic] + longueur[ic] - 1),
    };
  });
  return { croisement, hauteur, largeur, accroche: { x: dispo.beliere[0], y: dispo.beliere[1], lettre: 0 }, futs };
}

/** Ombre de contact sur le papier : silhouette floue du pendentif, très douce, décalée en bas à droite. */
function textureOmbreFond(dispo, glyphes, accroche) {
  const cap = glyphes.capHeight;
  const [x0, y0, x1, y1] = dispo.boite;
  const flou = 0.03 * cap;
  const marge = 3.4 * flou;
  const pied = accroche ? accroche.y - BELIERE.morsure * cap : 0;
  const sommet = accroche ? Math.max(y1, pied + BELIERE.hauteur * cap) : y1;
  const b = [x0 - marge, y0 - marge, x1 + marge, sommet + marge];
  const sc = 360 / (b[3] - b[1]);
  const W = Math.max(2, Math.ceil((b[2] - b[0]) * sc));
  const H = Math.max(2, Math.ceil((b[3] - b[1]) * sc));
  const toile = document.createElement('canvas');
  toile.width = W;
  toile.height = H;
  const ctx = toile.getContext('2d');
  ctx.shadowColor = ENCRE;
  ctx.shadowBlur = flou * sc;
  ctx.shadowOffsetX = 4 * W;
  ctx.setTransform(sc, 0, 0, -sc, -b[0] * sc - 4 * W, b[3] * sc);
  ctx.fill(chemin2D(dispo.lettres[0].glyphe, 0));
  ctx.fill(chemin2D(dispo.lettres[1].glyphe, dispo.lettres[1].x, dispo.lettres[1].y));
  if (accroche) {
    // silhouette de la bélière tube
    ctx.beginPath();
    for (const [x, y] of contourBeliere()) ctx.lineTo(accroche.x + x * cap, pied + y * cap);
    ctx.closePath();
    ctx.fill();
  }
  const texture = new THREE.CanvasTexture(toile);
  texture.colorSpace = THREE.SRGBColorSpace;
  return { texture, boite: b };
}

/** Ombre douce de la seconde lettre sur la face de la première, hors biseau. */
function textureOmbrePortee(dispo, glyphes) {
  const cap = glyphes.capHeight;
  const g1 = dispo.lettres[0].glyphe;
  const [bx0, by0, bx1, by1] = g1.boite;
  const sc = 400 / Math.max(bx1 - bx0, by1 - by0);
  const W = Math.max(2, Math.ceil((bx1 - bx0) * sc));
  const H = Math.max(2, Math.ceil((by1 - by0) * sc));
  const toile = document.createElement('canvas');
  toile.width = W;
  toile.height = H;
  const ctx = toile.getContext('2d');
  const transformer = (decalageX) => ctx.setTransform(sc, 0, 0, -sc, -bx0 * sc + decalageX, by1 * sc);
  const lettre1 = chemin2D(g1, 0);
  const lettre2 = chemin2D(dispo.lettres[1].glyphe, dispo.lettres[1].x, dispo.lettres[1].y);
  ctx.save();
  transformer(-3 * W);
  ctx.shadowColor = '#000';
  ctx.shadowBlur = 0.045 * cap * sc;
  ctx.shadowOffsetX = 3 * W + 0.012 * cap * sc;
  ctx.shadowOffsetY = 0.016 * cap * sc;
  ctx.fill(lettre2);
  ctx.restore();
  transformer(0);
  ctx.globalCompositeOperation = 'destination-in';
  ctx.fill(lettre1);
  ctx.globalCompositeOperation = 'destination-out';
  ctx.lineWidth = 2.4 * BISEAU * cap;
  ctx.stroke(lettre1);
  const texture = new THREE.CanvasTexture(toile);
  texture.colorSpace = THREE.SRGBColorSpace;
  return { texture, boite: g1.boite };
}

/** Scène du studio procédural (pièce sombre et neutre, boîtes douces en dégradé, filets nets) et ses jetables. */
function sceneStudio(definition) {
  const studio = new THREE.Scene();
  const jetables = [];
  const geoPiece = new THREE.SphereGeometry(40, 48, 24);
  const matPiece = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    uniforms: { sol: { value: definition.sol }, fond: { value: definition.fond }, plafond: { value: definition.plafond } },
    vertexShader: 'varying vec3 vDir; void main() { vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
    fragmentShader: 'uniform float sol; uniform float fond; uniform float plafond; varying vec3 vDir;'
      + 'void main() { float h = normalize(vDir).y; float v = h < 0.0 ? mix(fond, sol, smoothstep(0.0, -0.6, h)) : mix(fond, plafond, smoothstep(0.15, 0.95, h));'
      + ' gl_FragColor = vec4(vec3(v), 1.0); }',
  });
  studio.add(new THREE.Mesh(geoPiece, matPiece));
  jetables.push(geoPiece, matPiece);
  const plan = new THREE.PlaneGeometry(1, 1);
  jetables.push(plan);
  const rayon = 10;
  for (const pan of definition.panneaux) {
    const d = pan.d ?? 0.35;
    const dy = Math.max(d, 0.35) / 2;
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        intensite: { value: pan.i },
        bords: { value: new THREE.Vector3(d, pan.b ?? dy, pan.t ?? dy) },
        degrade: { value: new THREE.Vector2(pan.gx || 0, pan.gy || 0) },
      },
      vertexShader: 'varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
      fragmentShader: 'uniform float intensite; uniform vec3 bords; uniform vec2 degrade; varying vec2 vUv;'
        + 'void main() { float fx = 1.0 - smoothstep(1.0 - bords.x, 1.0, abs(vUv.x - 0.5) * 2.0);'
        + ' float fb = smoothstep(0.0, max(bords.y, 0.001), vUv.y); float fh = 1.0 - smoothstep(1.0 - max(bords.z, 0.001), 1.0, vUv.y);'
        + ' float g = max(0.0, 1.0 + degrade.x * (2.0 * vUv.x - 1.0)) * max(0.0, 1.0 + degrade.y * (2.0 * vUv.y - 1.0));'
        + ' gl_FragColor = vec4(vec3(intensite * fx * fb * fh * g), 1.0); }',
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      side: THREE.DoubleSide,
    });
    jetables.push(mat);
    const m = new THREE.Mesh(plan, mat);
    const a = degToRad(pan.a);
    const s = degToRad(pan.s);
    m.position.set(rayon * Math.sin(a) * Math.cos(s), rayon * Math.sin(s), rayon * Math.cos(a) * Math.cos(s));
    m.scale.set(2 * rayon * Math.tan(degToRad(Math.min(pan.l, 170)) / 2), 2 * rayon * Math.tan(degToRad(Math.min(pan.h, 170)) / 2), 1);
    m.lookAt(0, 0, 0);
    if (pan.r) m.rotateZ(degToRad(pan.r));
    studio.add(m);
  }
  return { studio, jetables };
}

/**
 * Prépare le préfiltrage PMREM sans rien compiler de façon synchrone : cibles et matières internes de PMREM créées,
 * puis tous les programmes (studio, filtre GGX, fond) compilés en parallèle (KHR_parallel_shader_compile). Le
 * préfiltrage lui-même (prefiltrerStudio) ne paie plus de compilation. Repli : tout dans prefiltrerStudio.
 */
async function preparerStudio(renderer, taille, definition) {
  const { studio, jetables } = sceneStudio(definition);
  const pmrem = new THREE.PMREMGenerator(renderer);
  const prepa = { studio, jetables, pmrem, taille };
  try {
    if (typeof pmrem._setSize === 'function' && typeof pmrem._allocateTargets === 'function') {
      pmrem._setSize(taille);
      const provisoire = pmrem._allocateTargets();
      provisoire.dispose();
      const compil = new THREE.Scene();
      const vide = new THREE.BufferGeometry();
      jetables.push(vide);
      for (const m of [pmrem._ggxMaterial, pmrem._blurMaterial]) if (m) compil.add(new THREE.Mesh(vide, m));
      // même matière que le fond de PMREM (« PMREM.Background ») : son programme est en cache au préfiltrage
      const fond = new THREE.MeshBasicMaterial({ side: THREE.BackSide, depthWrite: false, depthTest: false });
      jetables.push(fond);
      compil.add(new THREE.Mesh(vide, fond));
      for (const enfant of studio.children) compil.add(new THREE.Mesh(enfant.geometry, enfant.material));
      // mêmes variantes de programmes que pendant le préfiltrage : rendu dans une cible demi-flottante, sans tonalité
      const avant = renderer.getRenderTarget();
      renderer.setRenderTarget(pmrem._pingPongRenderTarget || null);
      const attente = renderer.compileAsync(compil, new THREE.PerspectiveCamera());
      renderer.setRenderTarget(avant);
      await attente;
    }
  } catch (erreur) {
    // compilation au préfiltrage
  }
  return prepa;
}

/** Préfiltrage PMREM du studio préparé : rendu des six faces et filtre GGX, programmes déjà compilés. */
function prefiltrerStudio(prepa) {
  const cible = prepa.cible || prepa.pmrem.fromScene(prepa.studio, 0, 0.1, 100, { size: prepa.taille });
  prepa.pmrem.dispose();
  for (const d of prepa.jetables) d.dispose();
  return cible;
}

/**
 * Première moitié du préfiltrage, dans une étape à part : les six faces du studio rendues dans la cible PMREM.
 * Utilise l'organisation interne de PMREMGenerator (three r186) ; si elle change, prefiltrerStudio fait tout.
 */
function rendreFacesStudio(renderer, prepa) {
  const pmrem = prepa.pmrem;
  if (typeof pmrem._sceneToCubeUV !== 'function' || typeof pmrem._applyPMREM !== 'function' || typeof pmrem._allocateTargets !== 'function') return;
  const xr = renderer.xr.enabled;
  try {
    renderer.xr.enabled = false;
    pmrem._setSize(prepa.taille);
    const cible = pmrem._allocateTargets();
    cible.depthBuffer = true;
    pmrem._sceneToCubeUV(prepa.studio, 0.1, 100, cible, new THREE.Vector3());
    prepa.faces = cible;
  } catch (erreur) {
    prepa.faces = null;
  } finally {
    renderer.setRenderTarget(null);
    renderer.xr.enabled = xr;
  }
}

/** Seconde moitié : filtre GGX par niveau de rugosité (PMREM), puis remise en état du rendu. */
function filtrerStudio(renderer, prepa) {
  const cible = prepa.faces;
  if (!cible) return;
  const xr = renderer.xr.enabled;
  try {
    renderer.xr.enabled = false;
    prepa.pmrem._applyPMREM(cible);
    cible.scissorTest = false;
    cible.viewport.set(0, 0, cible.width, cible.height);
    cible.scissor.set(0, 0, cible.width, cible.height);
    prepa.cible = cible;
  } catch (erreur) {
    cible.dispose();
    prepa.cible = null;
  } finally {
    renderer.setRenderTarget(null);
    renderer.xr.enabled = xr;
  }
}

/** Studio complet en une fois (retour de contexte, réglage du banc d'essai). */
function construireStudio(renderer, taille, definition) {
  const { studio, jetables } = sceneStudio(definition);
  return prefiltrerStudio({ studio, jetables, pmrem: new THREE.PMREMGenerator(renderer), taille });
}

function creerCalqueCotes(valeurs) {
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('class', 'mq-chiffre-3d__cotes');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  svg.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;overflow:visible;display:none';
  const groupe = document.createElementNS(NS, 'g');
  groupe.setAttribute('fill', 'none');
  groupe.setAttribute('stroke-width', '1');
  groupe.setAttribute('style', 'stroke:var(--encre,#1a1f2b)');
  const el = {};
  for (const nom of ['ligneV', 'traitV1', 'traitV2', 'ligneH', 'traitH1', 'traitH2']) {
    el[nom] = document.createElementNS(NS, 'line');
    groupe.append(el[nom]);
  }
  svg.append(groupe);
  for (const [nom, ancre, texte] of [['valeurV', 'end', valeurs.hauteur], ['valeurH', 'middle', valeurs.largeur]]) {
    el[nom] = document.createElementNS(NS, 'text');
    el[nom].setAttribute('text-anchor', ancre);
    el[nom].setAttribute('style', STYLE_VALEUR);
    el[nom].textContent = texte;
    svg.append(el[nom]);
  }
  return { svg, el };
}

/** Pose une ligne SVG sans créer de chaîne de caractères. */
function poserLigne(ligne, x1, y1, x2, y2) {
  ligne.x1.baseVal.value = x1;
  ligne.y1.baseVal.value = y1;
  ligne.x2.baseVal.value = x2;
  ligne.y2.baseVal.value = y2;
}

// --- Silhouette projetée (setMacro) : points en monde → écran, segments contre un rectangle ---

/** Projette (x, y, z) avec la matrice vue-projection `e` dans `sortie[o]`, `sortie[o + 1]` (px) ; NaN derrière la caméra. */
function projeter(e, W, H, x, y, z, sortie, o) {
  const w = e[3] * x + e[7] * y + e[11] * z + e[15];
  if (w <= 1e-6) {
    sortie[o] = NaN;
    sortie[o + 1] = NaN;
    return;
  }
  sortie[o] = ((e[0] * x + e[4] * y + e[8] * z + e[12]) / w + 1) * 0.5 * W;
  sortie[o + 1] = (1 - (e[1] * x + e[5] * y + e[9] * z + e[13]) / w) * 0.5 * H;
}

const LB = new Float64Array(2); // bornes t0, t1 du découpage de Liang-Barsky (aucune allocation)
function lbPas(p, q) {
  if (p === 0) return q >= 0;
  const t = q / p;
  if (p < 0) {
    if (t > LB[1]) return false;
    if (t > LB[0]) LB[0] = t;
  } else {
    if (t < LB[0]) return false;
    if (t < LB[1]) LB[1] = t;
  }
  return true;
}

/** Le segment (ax, ay)-(bx, by) touche-t-il le rectangle [x0, x1] × [y0, y1] ? (Liang-Barsky) */
function segmentDansRect(ax, ay, bx, by, x0, y0, x1, y1) {
  if (!(ax === ax && bx === bx)) return false; // NaN : point derrière la caméra
  if (Math.max(ax, bx) < x0 || Math.min(ax, bx) > x1 || Math.max(ay, by) < y0 || Math.min(ay, by) > y1) return false;
  if (ax >= x0 && ax <= x1 && ay >= y0 && ay <= y1) return true;
  LB[0] = 0;
  LB[1] = 1;
  const dx = bx - ax;
  const dy = by - ay;
  return lbPas(-dx, ax - x0) && lbPas(dx, x1 - ax) && lbPas(-dy, ay - y0) && lbPas(dy, y1 - ay) && LB[0] <= LB[1];
}

/**
 * Crée le chiffre 3D dans `conteneur` (fond transparent). Options : voir l'en-tête du fichier.
 * plein : la toile couvre tout le conteneur ; vues de repos et de face cadrées dans setCadre et setCadreFace.
 * Sans plein : pendant centré dans setCadre (ou dans la toile ; avec `debord`, cadre réduit d'autant, comme en v1).
 * Options supplémentaires facultatives : cadre { x, y, w, h } initial, accepterRenduLogiciel (prototype), debogage.
 */
export async function creerChiffre3D(conteneur, options = {}) {
  if (!conteneur || typeof conteneur.append !== 'function') throw new TypeError('Conteneur manquant');
  const {
    glyphesUrl = window.Marquoir && window.Marquoir.glyphesUrl,
    i1 = 'A',
    i2 = 'L',
    finition = 'doree',
    interactif = true,
    cotes = { hauteur: '[xx] mm', largeur: '[xx] mm' },
    plein = false,
    chaine = true,
    progression = 0,
    qualite = 'auto',
    debord = 1,
    cadre: cadreInitial = null,
    accepterRenduLogiciel = false,
    debogage = false,
    rotation = true,
    astuce = '',
  } = options;
  suivreGestes();
  // cotes: null → aucune cote (mesures pas encore relevées) : ni tracé, ni place réservée autour du pendant
  const sansCotes = cotes === null;
  const valeurs = { hauteur: '[xx] mm', largeur: '[xx] mm', ...(cotes || {}) };
  const marquer = debogage ? (nom) => performance.mark(`mq3d:${nom}`) : () => {};
  // coût de chaque étape de préparation en ms de fil principal (contrôle F04, docs/qa/v3-B)
  // [nom, durée (ms), début (performance.now), calme au début (ms sans geste)]
  const etapes = [];
  let debutEtape = 0;
  let calmeEtape = 0;
  const ouvrirEtape = () => {
    debutEtape = performance.now();
    calmeEtape = calmeDepuis();
  };
  const fermerEtape = (nom) => {
    etapes.push([nom, Math.round((performance.now() - debutEtape) * 10) / 10, Math.round(debutEtape), Math.min(99999, Math.round(calmeEtape))]);
    marquer(nom);
  };
  marquer('debut');
  const requeteReduit = window.matchMedia('(prefers-reduced-motion: reduce)');
  const requeteFin = window.matchMedia('(pointer: fine)');
  const reduit = () => requeteReduit.matches;

  // contexte WebGL 2, au calme : échec rapide, l'appelant garde le dessin
  await souffler();
  ouvrirEtape();
  const toile = document.createElement('canvas');
  toile.width = 1; // tampon alloué à sa taille finale pendant la chauffe, jamais avant
  toile.height = 1;
  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({
      canvas: toile,
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
      failIfMajorPerformanceCaveat: !accepterRenduLogiciel,
    });
  } catch (erreur) {
    throw new Error('WebGL indisponible', { cause: erreur });
  }
  fermerEtape('contexte');
  const abandonner = (erreur) => {
    renderer.dispose();
    renderer.forceContextLoss();
    throw erreur;
  };
  let glyphes;
  try {
    glyphes = await chargerGlyphes(glyphesUrl);
  } catch (erreur) {
    abandonner(erreur);
  }
  marquer('glyphes');
  const echelle = 1 / glyphes.capHeight;
  renderer.setClearColor(0x000000, 0);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.NeutralToneMapping ?? THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = EXPOSITION;

  // DOM : enveloppe, toile et calque des cotes superposés, jamais d'événement pointeur capté
  const enveloppe = document.createElement('div');
  enveloppe.className = 'mq-chiffre-3d';
  const positionne = plein && window.getComputedStyle(conteneur).position !== 'static';
  enveloppe.style.cssText = `${positionne ? 'position:absolute;inset:0' : 'position:relative;width:100%;height:100%'};pointer-events:none`;
  toile.className = 'mq-chiffre-3d__toile';
  toile.setAttribute('aria-hidden', 'true');
  toile.style.cssText = 'position:absolute;inset:0;display:block;width:100%;height:100%;outline:none';
  const calque = creerCalqueCotes(valeurs);
  enveloppe.append(toile, calque.svg);
  conteneur.append(enveloppe);

  // studio préfiltré, au calme : programmes compilés en parallèle, puis préfiltrage
  await souffler();
  ouvrirEtape();
  const taillePMREM = qualite === 'basse' ? 128 : 256;
  let definitionStudio = STUDIO;
  const prepaStudio = preparerStudio(renderer, taillePMREM, definitionStudio);
  fermerEtape('studio-compilation');
  const pretStudio = await prepaStudio;
  await souffler();
  ouvrirEtape();
  rendreFacesStudio(renderer, pretStudio);
  fermerEtape('studio-faces');
  await souffler();
  ouvrirEtape();
  filtrerStudio(renderer, pretStudio);
  let studio = prefiltrerStudio(pretStudio);
  fermerEtape('studio-filtre');

  const scene = new THREE.Scene();
  scene.environment = studio.texture;
  const camera = new THREE.PerspectiveCamera(FOV, 1, 0.02, 80);
  const cameraCalcul = new THREE.PerspectiveCamera(FOV, 1, 0.02, 80);

  const reglage = FINITIONS[finition] ? finition : 'doree';
  const matiere = new THREE.MeshStandardMaterial({ metalness: 1, roughness: FINITIONS[reglage].rugosite });
  matiere.color.setRGB(...FINITIONS[reglage].f0, THREE.LinearSRGBColorSpace);
  const nettete = { value: NETTETE_REFLETS };
  matiere.onBeforeCompile = (shader) => {
    const chunk = THREE.ShaderChunk.envmap_physical_pars_fragment;
    if (chunk && chunk.includes(CHUNK_REFLETS)) {
      shader.uniforms.uNettete = nettete;
      shader.fragmentShader = shader.fragmentShader.replace('#include <envmap_physical_pars_fragment>',
        `uniform float uNettete;\n${chunk.replace(CHUNK_REFLETS, 'envMapRotation * reflectVec, roughness * uNettete )')}`);
    }
  };
  matiere.customProgramCacheKey = () => 'mq-metal';
  // chaîne : même matière, avec une coupe nette facultative au plafond (gl_FragCoord.y, px de la toile, depuis le bas)
  const plafondChaine = { value: 1e9 };
  const matiereChaine = matiere.clone();
  matiereChaine.onBeforeCompile = (shader) => {
    matiere.onBeforeCompile(shader);
    shader.uniforms.uPlafond = plafondChaine;
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform float uPlafond;')
      .replace('#include <clipping_planes_fragment>', '#include <clipping_planes_fragment>\n\tif ( gl_FragCoord.y > uPlafond ) discard;');
  };
  matiereChaine.customProgramCacheKey = () => 'mq-chaine';

  const VIDE = new THREE.BufferGeometry();
  const lettre1 = new THREE.Mesh(VIDE, matiere);
  const lettre2 = new THREE.Mesh(VIDE, matiere);
  scene.add(lettre1, lettre2);

  // bélière et chaîne : même matière, chaîne opaque et jamais estompée (elle sort de la toile par le haut)
  const groupeChaine = new THREE.Group();
  let beliere = null;
  let liens = null;
  let maillon = null;
  let brins = [];
  if (chaine) {
    beliere = new THREE.Mesh(geometrieBeliere(), matiere);
    maillon = geometrieMaille(MAILLE);
    liens = new THREE.InstancedMesh(maillon, matiereChaine, nombreMailles());
    // la maille de la bélière passe dans le haut du tube : les deux brins en sortent de part et d'autre du dôme
    liens.position.y = BELIERE.hauteur - BELIERE_MAILLE * BELIERE.dome;
    brins = poserChaine(liens);
    groupeChaine.add(beliere, liens);
  }

  const plan = new THREE.PlaneGeometry(1, 1);
  const ombreFond = new THREE.Mesh(plan, new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, opacity: OPACITE_OMBRE, toneMapped: false }));
  ombreFond.renderOrder = -1;
  const portee = new THREE.Mesh(plan, new THREE.MeshBasicMaterial({
    color: 0x000000,
    transparent: true,
    depthWrite: false,
    opacity: OPACITE_PORTEE,
    toneMapped: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  }));
  scene.add(ombreFond, portee);

  const geometries = new Map();
  const geometrie = (lettre) => {
    if (!geometries.has(lettre)) geometries.set(lettre, construireLettre(glyphes.lettres[lettre], echelle));
    return geometries.get(lettre);
  };

  const etat = {
    lettres: ['A', 'L'],
    dims: { l: 1, h: 1 },
    hCroisement: 0.5,
    lCroisement: 0.5,
    rects: new Float64Array(0),
    futs: [null, null],
    images: 0,
    p: clamp(Number(progression) || 0, 0, 1),
    pVue: requeteReduit.matches ? 1 : clamp(Number(progression) || 0, 0, 1),
    lisser: false,
    temps: 0,
    balL: 0,
    balT: 0,
    inclL: 0,
    inclT: 0,
    tourL: 0,
    tourT: 0,
    vitL: 0,
    glisse: null,
    retour: false,
    cibleL: 0,
    cibleT: 0,
    poseDebut: null,
    poseAvancement: 1,
    trace: 1,
    traceDebut: null,
    cotes: reduit(),
    finition: null,
    largeur: 0,
    hauteur: 0,
    dpr: 0,
    plafondDpr: 2,
    moyenne: 0.016,
    lentes: 0,
    reallocations: 0,
    cadre: cadreInitial && cadreInitial.w > 0 && cadreInitial.h > 0 ? { x: +cadreInitial.x, y: +cadreInitial.y, w: +cadreInitial.w, h: +cadreInitial.h } : null,
    cadreFace: null,
    cadreCalc: { x: 0, y: 0, w: 1, h: 1 },
    candidats: null,
    macro: null,
    macroEmis: '',
    premiereImage: null,
    clesSales: true,
    encore: false,
    forcer: false,
    traceManuel: false,
    pret: false,
    pauseManuelle: false,
    visible: true,
    perdu: false,
    detruit: false,
    disposition: null,
    cleCandidats: '',
    plafond: null,
  };

  // points remarquables, en coordonnées monde
  const croisement = new THREE.Vector3(); // croisement, sur la face avant : point fixe de la caméra, cible de la macro
  const ancre = croisement;
  const pointHaut = new THREE.Vector3();
  const pointCentre = new THREE.Vector3();
  const base2 = new THREE.Vector3();
  let pointsCadrage = new Float32Array(0); // lettres, bélière et départ de la chaîne (vues plein cadre)
  let pointsPendant = new Float32Array(0); // lettres et bélière (mode non plein : pendant centré)
  // silhouette pour setMacro : contours des deux lettres (faces avant et arrière), bélière, brins de chaîne
  const silhouette = { lettres: [], beliere: null, brins: [] };
  let ecran = new Float64Array(256);

  // canaux de la caméra aux six clés : lacet, tangage, log(échelle), x, y (px) du croisement, poids du cadre,
  // rotation de l'environnement
  const NC = CLES.length;
  const NCANAUX = 7;
  const canaux = new Float64Array(NCANAUX * NC);
  const tangentes = new Float64Array(NCANAUX * NC);
  const valeursCles = Array.from({ length: NCANAUX }, () => new Float64Array(NC));
  const vue = { lacet: 0, tangage: 0, s: 1, sx: 0, sy: 0, wc: 1, env: 0 };
  const cleRepos = { sx: 0, sy: 0, s: 1 };
  const cleFace = { sx: 0, sy: 0, s: 1 };
  const boite = { x0: 0, y0: 0, x1: 0, y1: 0 };
  const zoneCalc = { x0: 0, y0: 0, x1: 0, y1: 0 };
  const cadreToile = { x: 0, y: 0, w: 1, h: 1 };
  const infoImage = { progression: 0, macro: 0, largeur: 0, hauteur: 0 };
  const abonnes = [];

  const vecteur = new THREE.Vector3();
  const avant = new THREE.Vector3();
  const droite = new THREE.Vector3();
  const haut = new THREE.Vector3();
  const cible = new THREE.Vector3();
  const mVP = new THREE.Matrix4();

  /** Caméra telle que le croisement soit en (sx, sy) px, à raison de s px par unité, vue (lacet, tangage). */
  function poserCamera(cam, lacet, tangage, sx, sy, s) {
    const W = etat.largeur;
    const H = etat.hauteur;
    const distance = H / 2 / (s * Math.tan(degToRad(FOV / 2)));
    avant.set(Math.sin(lacet) * Math.cos(tangage), Math.sin(tangage), Math.cos(lacet) * Math.cos(tangage));
    droite.set(Math.cos(lacet), 0, -Math.sin(lacet));
    haut.crossVectors(avant, droite);
    cible.copy(ancre).addScaledVector(droite, -(sx - W / 2) / s).addScaledVector(haut, (sy - H / 2) / s);
    cam.position.copy(cible).addScaledVector(avant, distance);
    cam.lookAt(cible);
    cam.updateMatrixWorld();
  }

  function boiteProjetee(cam, points) {
    boite.x0 = Infinity;
    boite.y0 = Infinity;
    boite.x1 = -Infinity;
    boite.y1 = -Infinity;
    for (let i = 0; i < points.length; i += 3) {
      vecteur.set(points[i], points[i + 1], points[i + 2]).project(cam);
      const x = ((vecteur.x + 1) / 2) * etat.largeur;
      const y = ((1 - vecteur.y) / 2) * etat.hauteur;
      if (x < boite.x0) boite.x0 = x;
      if (x > boite.x1) boite.x1 = x;
      if (y < boite.y0) boite.y0 = y;
      if (y > boite.y1) boite.y1 = y;
    }
  }

  /** Cadre la vue (lacet, tangage) dans la zone { x0, y0, x1, y1 } : position du croisement et échelle. */
  function ajuster(lacet, tangage, zone, sortie, points) {
    const zw = Math.max(24, zone.x1 - zone.x0);
    const zh = Math.max(24, zone.y1 - zone.y0);
    const zcx = (zone.x0 + zone.x1) / 2;
    const zcy = (zone.y0 + zone.y1) / 2;
    let s = 0.8 * Math.min(zw / etat.dims.l, zh / (etat.dims.h * 1.3));
    let sx = zcx;
    let sy = zcy;
    for (let k = 0; k < 6; k += 1) {
      poserCamera(cameraCalcul, lacet, tangage, sx, sy, s);
      boiteProjetee(cameraCalcul, points);
      const f = Math.min(zw / Math.max(1, boite.x1 - boite.x0), zh / Math.max(1, boite.y1 - boite.y0));
      s *= f;
      sx += zcx - (sx + ((boite.x0 + boite.x1) / 2 - sx) * f);
      sy += zcy - (sy + ((boite.y0 + boite.y1) / 2 - sy) * f);
    }
    sortie.sx = sx;
    sortie.sy = sy;
    sortie.s = s;
  }

  function zone(x0, y0, x1, y1) {
    zoneCalc.x0 = x0;
    zoneCalc.y0 = y0;
    zoneCalc.x1 = x1;
    zoneCalc.y1 = y1;
    return zoneCalc;
  }

  function cadreEffectif() {
    const W = etat.largeur;
    const H = etat.hauteur;
    if (etat.cadre) return etat.cadre;
    cadreToile.x = 0;
    cadreToile.y = 0;
    cadreToile.w = W;
    cadreToile.h = H;
    if (!plein && debord > 1) {
      cadreToile.w = W / debord;
      cadreToile.h = H / debord;
      cadreToile.x = (W - cadreToile.w) / 2;
      cadreToile.y = (H - cadreToile.h) / 2;
    }
    return cadreToile;
  }

  /** Zone du pendant en mode non plein : le cadre moins 7 %, et au moins la place des cotes au bord de la toile. */
  function zoneNonPleine(c) {
    const W = etat.largeur;
    const H = etat.hauteur;
    const m = Math.max(12, 0.07 * Math.min(c.w, c.h));
    if (sansCotes) return zone(c.x + m, c.y + m, c.x + c.w - m, c.y + c.h - m);
    const e = clamp(Math.min(W, H) / 480, 0.6, 1);
    // la valeur des cotes garde 13 px : à gauche, cote verticale et « [xx] mm » (96 px), en bas, cote horizontale et
    // sa valeur (56 px) ; à droite et en haut, autant sur un grand aperçu pour rester centré, moins sur un petit
    const x0 = Math.max(c.x + m, 96);
    const y0 = Math.max(c.y + m, Math.max(24, 56 * e));
    const x1 = Math.min(c.x + c.w - m, W - Math.max(24, 96 * e));
    const y1 = Math.min(c.y + c.h - m, H - 56);
    if (x1 - x0 < 48 || y1 - y0 < 48) return zone(c.x + m, c.y + m, c.x + c.w - m, c.y + c.h - m);
    return zone(x0, y0, x1, y1);
  }

  /** Clés de la chorégraphie pour la taille, les cadres, la macro retenue et les lettres du moment. */
  function recalculerCles() {
    etat.clesSales = false;
    const W = etat.largeur;
    const H = etat.hauteur;
    cameraCalcul.aspect = W / H;
    cameraCalcul.updateProjectionMatrix();
    const c = cadreEffectif();
    const f = etat.cadreFace || c;
    etat.cadreCalc = c;
    if (plein) {
      // petit cadre (téléphone, ≈ 150 à 250 px de haut) : marge réduite, le pendant prend la place ; sinon 7 % (≥ 16 px)
      const petit = Math.min(c.w, c.h) < 260;
      const marge = petit ? Math.max(6, 0.04 * Math.min(c.w, c.h)) : Math.max(16, 0.07 * Math.min(c.w, c.h));
      ajuster(LACETS[0], TANGAGES[0], zone(c.x + marge, c.y + marge, c.x + c.w - marge, c.y + c.h - marge), cleRepos, pointsCadrage);
      const e = clamp(Math.min(f.w, f.h) / 480, 0.6, 1);
      const m = 0.03 * Math.min(f.w, f.h);
      // vue de face : à gauche et en bas, la place des cotes (sauf sans cotes : marges égales)
      if (sansCotes) ajuster(0, 0, zone(f.x + 28 * e + m, f.y + 28 * e + m, f.x + f.w - 28 * e - m, f.y + f.h - 28 * e - m), cleFace, pointsCadrage);
      else ajuster(0, 0, zone(f.x + 104 * e + m, f.y + 28 * e + m, f.x + f.w - 28 * e - m, f.y + f.h - 72 * e - m), cleFace, pointsCadrage);
    } else {
      // non plein (fiche) : pendant centré dans le cadre ; la place des cotes est réservée au bord de la toile, des deux
      // côtés, pour que le centrage ne bouge pas quand elles paraissent et qu'elles ne sortent jamais de la toile
      ajuster(LACETS[0], TANGAGES[0], zoneNonPleine(c), cleRepos, pointsPendant);
      ajuster(0, 0, zoneNonPleine(f), cleFace, pointsPendant);
    }
    let sMacro;
    let sxMacro;
    let syMacro;
    const m = etat.macro;
    if (m) {
      // setMacro : croisement posé sur la cible du candidat retenu, à l'échelle trouvée (px par unité)
      sMacro = m.s;
      sxMacro = m.cible.x;
      syMacro = m.cible.y;
    } else {
      // par défaut : le croisement remplit ≈ 70 % de la hauteur (au moins ×3), sans déborder d'un écran étroit,
      // et l'objet est centré verticalement, sans papier vide sous lui
      sMacro = Math.min(Math.max((MACRO_DEFAUT.remplissage * H) / etat.hCroisement, MACRO_DEFAUT.mini * cleRepos.s), (0.65 * W) / etat.lCroisement);
      sxMacro = W * (W > H ? 0.54 : 0.5);
      const dessous = (croisement.y + etat.dims.h / 2) * sMacro;
      syMacro = clamp(0.96 * H - dessous, 0.3 * H, 0.7 * H);
    }
    const remplir = (k, a, b, c2, d, e2, f2) => {
      const v = valeursCles[k];
      v[0] = a;
      v[1] = b;
      v[2] = c2;
      v[3] = d;
      v[4] = e2;
      v[5] = f2;
    };
    for (let i = 0; i < NC; i += 1) {
      valeursCles[0][i] = LACETS[i];
      valeursCles[1][i] = TANGAGES[i];
      valeursCles[6][i] = ROTATIONS_ENV[i];
    }
    remplir(2, Math.log(cleRepos.s), Math.log(cleRepos.s), Math.log(sMacro), Math.log(sMacro * MACRO.poussee), Math.log(cleFace.s), Math.log(cleFace.s));
    remplir(3, cleRepos.sx, cleRepos.sx, sxMacro, sxMacro, cleFace.sx, cleFace.sx);
    remplir(4, cleRepos.sy, cleRepos.sy, syMacro, syMacro, cleFace.sy, cleFace.sy);
    remplir(5, 1, 1, 0, 0, 1, 1);
    for (let k = 0; k < NCANAUX; k += 1) {
      for (let i = 0; i < NC; i += 1) canaux[k * NC + i] = valeursCles[k][i];
      pentes(CLES, canaux, k * NC, tangentes);
    }
  }

  function evaluerVue(p) {
    vue.lacet = hermite(CLES, canaux, 0, tangentes, p);
    vue.tangage = hermite(CLES, canaux, NC, tangentes, p);
    vue.s = Math.exp(hermite(CLES, canaux, 2 * NC, tangentes, p));
    vue.sx = hermite(CLES, canaux, 3 * NC, tangentes, p);
    vue.sy = hermite(CLES, canaux, 4 * NC, tangentes, p);
    vue.wc = clamp(hermite(CLES, canaux, 5 * NC, tangentes, p), 0, 1);
    vue.env = hermite(CLES, canaux, 6 * NC, tangentes, p);
  }

  /** Silhouette du pendant en monde (setMacro) : contours des lettres, ligne de la bélière, centres des mailles. */
  function construireSilhouette(d) {
    let plus = 0;
    silhouette.lettres = d.lettres.map((l, n) => {
      const ox = n === 0 ? lettre1.position.x : base2.x;
      const oy = n === 0 ? lettre1.position.y : base2.y;
      const z = n === 0 ? Z1 : Z2;
      const contours = [];
      for (const commandes of lireContours(l.glyphe.contours)) {
        const c = echantillonner(commandes, echelle, 6);
        if (!c) continue;
        const xy = new Float64Array(c.n * 2);
        for (let i = 0; i < c.n; i += 1) {
          xy[2 * i] = c.x[i] + ox;
          xy[2 * i + 1] = c.y[i] + oy;
        }
        contours.push(xy);
        plus = Math.max(plus, c.n);
      }
      return { contours, zAvant: z + EPAISSEUR / 2, zArriere: z - EPAISSEUR / 2 };
    });
    silhouette.beliere = null;
    silhouette.brins = [];
    if (beliere) {
      // silhouette de face de la bélière tube, refermée, sur son plan médian
      const forme = contourBeliere();
      const pts = new Float64Array((forme.length + 1) * 3);
      for (let i = 0; i <= forme.length; i += 1) {
        const [x, y] = forme[i % forme.length];
        vecteur.set(x, y, 0).applyMatrix4(beliere.matrixWorld);
        pts[3 * i] = vecteur.x;
        pts[3 * i + 1] = vecteur.y;
        pts[3 * i + 2] = vecteur.z;
      }
      silhouette.beliere = pts;
      const m = new THREE.Matrix4();
      const centre = (k, sortie, o) => {
        liens.getMatrixAt(k, m);
        vecteur.setFromMatrixPosition(m).applyMatrix4(liens.matrixWorld);
        sortie[o] = vecteur.x;
        sortie[o + 1] = vecteur.y;
        sortie[o + 2] = vecteur.z;
      };
      silhouette.brins = brins.map(([a, b]) => {
        const pts2 = new Float64Array((b - a + 1) * 3);
        centre(0, pts2, 0);
        for (let k = a; k < b; k += 1) centre(k, pts2, 3 * (k - a + 1));
        plus = Math.max(plus, b - a + 1);
        return pts2;
      });
      plus = Math.max(plus, forme.length + 1);
    }
    if (ecran.length < 2 * plus) ecran = new Float64Array(2 * plus);
  }

  /**
   * Nombre d'éléments de silhouette (sommets ou segments des contours, de la bélière et des brins) qui entrent dans
   * le rectangle R, pour la caméra `cam` ; `s` : px par unité (épaisseur du fil et des mailles). Arrêt au premier
   * si `compter` est faux.
   */
  function intrusion(cam, R, s, compter) {
    mVP.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
    const e = mVP.elements;
    const W = etat.largeur;
    const H = etat.hauteur;
    const cx = (R.x0 + R.x1) / 2;
    const cy = (R.y0 + R.y1) / 2;
    let n = 0;
    for (const L of silhouette.lettres) {
      let croise = 0; // parité du centre de R dans la face avant (lettre qui couvrirait toute la réserve)
      for (let face = 0; face < 2; face += 1) {
        const z = face === 0 ? L.zAvant : L.zArriere;
        for (const xy of L.contours) {
          const np = xy.length / 2;
          for (let i = 0; i < np; i += 1) projeter(e, W, H, xy[2 * i], xy[2 * i + 1], z, ecran, 2 * i);
          for (let i = 0, j = np - 1; i < np; j = i, i += 1) {
            const ax = ecran[2 * j];
            const ay = ecran[2 * j + 1];
            const bx = ecran[2 * i];
            const by = ecran[2 * i + 1];
            if (segmentDansRect(ax, ay, bx, by, R.x0, R.y0, R.x1, R.y1)) {
              n += 1;
              if (!compter) return n;
            }
            if (face === 0 && (ay > cy) !== (by > cy) && cx < ((bx - ax) * (cy - ay)) / (by - ay) + ax) croise += 1;
          }
        }
      }
      if (croise % 2 === 1) {
        n += 1;
        if (!compter) return n;
      }
    }
    const lignes = silhouette.beliere ? [silhouette.beliere, ...silhouette.brins] : silhouette.brins;
    for (let q = 0; q < lignes.length; q += 1) {
      const pts = lignes[q];
      const t = (q === 0 && silhouette.beliere ? BELIERE_TRAIT : DEMI_MAILLE) * s;
      const np = pts.length / 3;
      for (let i = 0; i < np; i += 1) projeter(e, W, H, pts[3 * i], pts[3 * i + 1], pts[3 * i + 2], ecran, 2 * i);
      for (let i = 1; i < np; i += 1) {
        if (segmentDansRect(ecran[2 * i - 2], ecran[2 * i - 1], ecran[2 * i], ecran[2 * i + 1], R.x0 - t, R.y0 - t, R.x1 + t, R.y1 + t)) {
          n += 1;
          if (!compter) return n;
        }
      }
    }
    return n;
  }

  /**
   * Plus petit écart (px) entre la silhouette visible et le rectangle R, aux trois poses de la macro : mesure du
   * papier vide laissé entre l'objet et la réserve (le meilleur candidat remplit la zone libre).
   */
  function ecartMacro(c, z) {
    const s = z * cleRepos.s;
    const W = etat.largeur;
    const H = etat.hauteur;
    const R = c.R;
    let ecart = Infinity;
    for (let q = 0; q < 3; q += 1) {
      const t = q / 2;
      poserCamera(cameraCalcul, lerp(LACETS[2], LACETS[3], t), lerp(TANGAGES[2], TANGAGES[3], t), c.cible.x, c.cible.y, s * MACRO.poussee ** t);
      mVP.multiplyMatrices(cameraCalcul.projectionMatrix, cameraCalcul.matrixWorldInverse);
      const e = mVP.elements;
      for (const L of silhouette.lettres) {
        for (const xy of L.contours) {
          for (let i = 0; i < xy.length; i += 2) {
            projeter(e, W, H, xy[i], xy[i + 1], L.zAvant, ecran, 0);
            const x = ecran[0];
            const y = ecran[1];
            if (!(x >= 0 && x <= W && y >= 0 && y <= H)) continue;
            const dx = Math.max(R.x0 - x, 0, x - R.x1);
            const dy = Math.max(R.y0 - y, 0, y - R.y1);
            const d = Math.hypot(dx, dy);
            if (d < ecart) ecart = d;
          }
        }
      }
    }
    return ecart === Infinity ? Math.max(W, H) : ecart;
  }

  /** Intrusion de la macro au zoom z (par rapport au repos) aux trois poses tenues : début, milieu, fin de macro. */
  function intrusionMacro(c, z, compter) {
    const s = z * cleRepos.s;
    let n = 0;
    for (let q = 0; q < 3; q += 1) {
      const t = q / 2;
      const sq = s * MACRO.poussee ** t;
      poserCamera(cameraCalcul, lerp(LACETS[2], LACETS[3], t), lerp(TANGAGES[2], TANGAGES[3], t), c.cible.x, c.cible.y, sq);
      n += intrusion(cameraCalcul, c.R, sq, compter);
      if (n && !compter) return n;
    }
    return n;
  }

  /** Étendue verticale (px de la toile) des lettres à la pose médiane de la macro, cible c.cible, zoom z. */
  function etendueMacro(c, z) {
    const s = z * cleRepos.s;
    poserCamera(cameraCalcul, lerp(LACETS[2], LACETS[3], 0.5), lerp(TANGAGES[2], TANGAGES[3], 0.5), c.cible.x, c.cible.y, s * MACRO.poussee ** 0.5);
    mVP.multiplyMatrices(cameraCalcul.projectionMatrix, cameraCalcul.matrixWorldInverse);
    const e = mVP.elements;
    let min = Infinity;
    let max = -Infinity;
    for (const L of silhouette.lettres) {
      for (const xy of L.contours) {
        for (let i = 0; i < xy.length; i += 2) {
          projeter(e, etat.largeur, etat.hauteur, xy[i], xy[i + 1], L.zAvant, ecran, 0);
          if (ecran[1] < min) min = ecran[1];
          if (ecran[1] > max) max = ecran[1];
        }
      }
    }
    return { min, max };
  }

  /**
   * Équilibre vertical de la macro retenue : pas de bande de papier vide SOUS les lettres quand la place existe (le haut
   * n'est jamais vide : la bélière et la chaîne y montent, on ne les pousse donc pas hors champ). La zone libre est la
   * toile, moins une réserve en bande pleine largeur (mobile). Une lettre plus haute que la zone descend jusqu'à 6 % du
   * bas ; une lettre plus petite descend jusqu'au centre. Le décalage ne fait que descendre l'objet, la réserve reste
   * libre (dichotomie vers 0) et le croisement reste au-dessus de 85 % de la hauteur. Renvoie le décalage (px).
   */
  function equilibrerMacro(c, z) {
    const W = etat.largeur;
    const H = etat.hauteur;
    const R = c.R;
    let haut0 = 0;
    let bas0 = H;
    if (R && R.x0 <= 1 && R.x1 >= W - 1) {
      if (R.y0 <= 1) haut0 = R.y1;
      else if (R.y1 >= H - 1) bas0 = R.y0;
    }
    const Hf = bas0 - haut0;
    if (Hf <= 0) return 0;
    const { min, max } = etendueMacro(c, z);
    if (!Number.isFinite(min) || !Number.isFinite(max)) return 0;
    const m = 0.06 * Hf;
    const vHaut = min - haut0;
    const vBas = bas0 - max;
    let d = max - min <= Hf - 2 * m ? (vBas - vHaut) / 2 : vBas - m;
    d = Math.min(Math.max(0, d), Math.max(0, 0.85 * H - c.cible.y));
    if (Math.abs(d) < 4) return 0;
    const essai = (dy) => intrusionMacro({ cible: { x: c.cible.x, y: c.cible.y + dy }, R }, z, false) === 0;
    if (!R || essai(d)) return d;
    let ok = 0;
    let ko = d;
    for (let i = 0; i < 6; i += 1) {
      const mid = (ok + ko) / 2;
      if (essai(mid)) ok = mid;
      else ko = mid;
    }
    return ok;
  }

  /** Macro la plus serrée d'un candidat : ×3 visé, au moins ×2 ; au-dessus de ×3 seulement si rien ne passe en dessous. */
  function resoudreCandidat(c) {
    if (!c.R) return { zoom: MACRO.vise, faisable: true, intrusion: 0 };
    const G = [2, 2.25, 2.5, 2.75, 3, 3.5, 4, 4.5, 5];
    const r = G.map((z) => intrusionMacro(c, z, false));
    const affiner = (ok, ko) => {
      let a = ok;
      let b = ko;
      for (let i = 0; i < 5; i += 1) {
        const mid = (a + b) / 2;
        if (intrusionMacro(c, mid, false) === 0) a = mid;
        else b = mid;
      }
      return a;
    };
    let iOk = -1;
    for (let i = 0; i < G.length && G[i] <= MACRO.vise; i += 1) if (r[i] === 0) iOk = i;
    const avecEcart = (z, extra) => ({ zoom: z, faisable: true, intrusion: 0, ecart: Math.round(ecartMacro(c, z)), ...extra });
    if (iOk >= 0) {
      const z = G[iOk] < MACRO.vise && r[iOk + 1] > 0 ? affiner(G[iOk], G[iOk + 1]) : G[iOk];
      return avecEcart(z);
    }
    for (let i = 0; i < G.length; i += 1) {
      if (G[i] > MACRO.vise && r[i] === 0) return avecEcart(affiner(G[i], G[i - 1]));
    }
    // rien à ×2 ou plus : la lisibilité de l'annotation passe avant la force du zoom
    for (let z = MACRO.mini - 0.05; z >= MACRO.plancher - 1e-6; z -= 0.05) {
      if (intrusionMacro(c, z, false) === 0) return avecEcart(z, { sousMini: true });
    }
    let iMin = 0;
    const comptes = G.map((z) => intrusionMacro(c, z, true));
    for (let i = 1; i < G.length; i += 1) if (comptes[i] < comptes[iMin]) iMin = i;
    return { zoom: G[iMin], faisable: false, intrusion: comptes[iMin] };
  }

  let macroPrevue = false;
  /** Calcul de setMacro au calme et pendant le temps libre ; relancé si un geste survient entre deux candidats. */
  function planifierMacro() {
    if (macroPrevue || etat.detruit || !etat.candidats) return;
    macroPrevue = true;
    souffler().then(() => {
      macroPrevue = false;
      if (!etat.detruit) resoudreMacro();
    });
  }

  function resoudreMacro() {
    const liste = etat.candidats;
    if (!liste || !etat.largeur || !etat.hauteur || !silhouette.lettres.length) return null;
    if (!liste.length) {
      if (etat.macro) {
        etat.macro = null;
        etat.clesSales = true;
        etat.forcer = true;
        demander();
      }
      return null;
    }
    const t0 = performance.now();
    if (etat.clesSales) recalculerCles();
    const W = etat.largeur;
    const H = etat.hauteur;
    const prepares = liste.map((cand) => {
      const r = cand.reserve;
      const R = r ? {
        x0: Math.max(0, r.x - MACRO.marge),
        y0: Math.max(0, r.y - MACRO.marge),
        x1: Math.min(W, r.x + r.w + MACRO.marge),
        y1: Math.min(H, r.y + r.h + MACRO.marge),
      } : null;
      return { cible: cand.cible, R: R && R.x1 > R.x0 && R.y1 > R.y0 ? R : null };
    });
    const resultats = prepares.map((c) => resoudreCandidat(c));
    let indice = 0;
    // sans recouvrement d'abord, au moins ×2 ensuite, puis le plus proche de ×3 (écart en log : ×2 et ×4,5 se valent),
    // et le moins de papier vide entre l'objet et la réserve
    const score = (r) => (r.faisable ? 100 : 0) + (r.zoom >= MACRO.mini - 1e-6 ? 10 : 0) - Math.abs(Math.log(r.zoom / MACRO.vise))
      - (r.faisable ? (MACRO.remplissage * r.ecart) / Math.max(W, H) : r.intrusion / 1e4);
    for (let i = 1; i < resultats.length; i += 1) if (score(resultats[i]) > score(resultats[indice]) + 1e-6) indice = i;
    const choix = resultats[indice];
    const zoom = Math.round(choix.zoom * 100) / 100;
    const cand = liste[indice];
    const decalage = choix.faisable ? equilibrerMacro(prepares[indice], choix.zoom) : 0;
    etat.macro = {
      indice,
      zoom,
      s: choix.zoom * cleRepos.s,
      cible: { x: cand.cible.x, y: cand.cible.y + decalage },
      decalage: Math.round(decalage),
      faisable: choix.faisable,
      intrusion: choix.intrusion,
      sousMini: Boolean(choix.sousMini),
      ms: Math.round((performance.now() - t0) * 100) / 100,
      calme: Math.round(calmeDepuis()),
      resultats: resultats.map((r) => ({ zoom: Math.round(r.zoom * 100) / 100, faisable: r.faisable, intrusion: r.intrusion, ecart: r.ecart })),
    };
    etat.clesSales = true;
    etat.forcer = true;
    demander();
    const cle = `${indice}:${zoom.toFixed(2)}`;
    if (cle !== etat.macroEmis) {
      etat.macroEmis = cle;
      conteneur.dispatchEvent(new CustomEvent('mq:chiffre-macro', { bubbles: true, detail: { indice, zoom } }));
    }
    return etat.macro;
  }

  function appliquerLettres(l1, l2, { ombres = true, analyse: dejaAnalyse = null } = {}) {
    etat.lettres = [l1, l2];
    const d = disposer(glyphes, l1, l2);
    const [x0, y0, x1, y1] = d.boite;
    const cx = (x0 + x1) / 2;
    const cy = (y0 + y1) / 2;
    lettre1.geometry = geometrie(l1);
    lettre2.geometry = geometrie(l2);
    lettre1.position.set(-cx * echelle, -cy * echelle, Z1);
    base2.set((d.lettres[1].x - cx) * echelle, (d.lettres[1].y - cy) * echelle, Z2);
    lettre2.position.copy(base2);
    etat.dims = { l: (x1 - x0) * echelle, h: (y1 - y0) * echelle };
    // boîtes des deux lettres sur leur face avant : les cotes suivent la silhouette visible
    etat.rects = Float64Array.from(d.lettres.flatMap((l, n) => {
      const bx = l.glyphe.boite;
      const z = (n === 0 ? Z1 : Z2) + EPAISSEUR / 2;
      return [(bx[0] + l.x - cx) * echelle, (bx[1] + l.y - cy) * echelle, (bx[2] + l.x - cx) * echelle, (bx[3] + l.y - cy) * echelle, z];
    }));
    const analyse = dejaAnalyse || analyserChiffre(d, glyphes);
    croisement.set((analyse.croisement[0] - cx) * echelle, (analyse.croisement[1] - cy) * echelle, Z2 + EPAISSEUR / 2);
    etat.hCroisement = clamp(analyse.hauteur, 0.36, 0.8);
    etat.lCroisement = clamp(analyse.largeur, 0.2, 1.2);
    etat.futs = analyse.futs.map((f, n) => (f ? {
      x: (f.x - cx) * echelle,
      demiLargeur: f.demiLargeur * echelle,
      haut: (f.haut - cy) * echelle,
      bas: (f.bas - cy) * echelle,
      z: (n === 0 ? Z1 : Z2) + EPAISSEUR / 2,
    } : null));
    pointCentre.set(0, 0, Z2 + EPAISSEUR / 2);
    const points = [];
    for (let n = 0; n < etat.rects.length; n += 5) {
      const [rx0, ry0, rx1, ry1, rz] = etat.rects.subarray(n, n + 5);
      for (const z of [rz, rz - EPAISSEUR]) for (const [x, y] of [[rx0, ry0], [rx1, ry0], [rx0, ry1], [rx1, ry1]]) points.push(x, y, z);
    }
    // bélière tube sur le sommet de la première lettre, centrée sur son épaisseur, un peu enfoncée ; chaîne au-dessus
    let accroche = null;
    if (chaine) {
      const a = analyse.accroche;
      groupeChaine.position.set(a.x * echelle, a.y * echelle - BELIERE.morsure, 0);
      lettre1.add(groupeChaine);
      scene.updateMatrixWorld(true);
      beliere.getWorldPosition(vecteur);
      for (const cote of [-1, 1]) points.push(vecteur.x + (cote * BELIERE.haut) / 2, vecteur.y + BELIERE.hauteur - BELIERE.dome, vecteur.z + BELIERE.profondeur / 2);
      pointHaut.copy(vecteur).y += BELIERE.hauteur;
      points.push(pointHaut.x, pointHaut.y, pointHaut.z);
      pointsPendant = Float32Array.from(points);
      points.push(pointHaut.x, pointHaut.y + 0.12, pointHaut.z);
      accroche = { x: a.x, y: a.y };
    } else {
      pointHaut.set(0, etat.dims.h / 2, Z2 + EPAISSEUR / 2);
      pointsPendant = Float32Array.from(points);
      scene.updateMatrixWorld(true);
    }
    pointsCadrage = Float32Array.from(points);
    construireSilhouette(d);
    etat.disposition = { d, accroche, cx, cy };
    if (ombres) poserOmbres();
    etat.clesSales = true;
    planifierMacro();
  }

  /** Ombres de la disposition courante : sur le papier, et de la seconde lettre sur la première. */
  function poserOmbres() {
    const { d, accroche, cx, cy } = etat.disposition;
    const ancienneFond = ombreFond.material.map;
    const anciennePortee = portee.material.map;
    try {
      const o = textureOmbreFond(d, glyphes, accroche);
      ombreFond.material.map = o.texture;
      ombreFond.scale.set((o.boite[2] - o.boite[0]) * echelle, (o.boite[3] - o.boite[1]) * echelle, 1);
      ombreFond.position.set(((o.boite[0] + o.boite[2]) / 2 - cx) * echelle + OMBRE_DECALAGE[0], ((o.boite[1] + o.boite[3]) / 2 - cy) * echelle + OMBRE_DECALAGE[1], Z_FOND);
      ombreFond.visible = true;
      const t = textureOmbrePortee(d, glyphes);
      portee.material.map = t.texture;
      portee.scale.set((t.boite[2] - t.boite[0]) * echelle, (t.boite[3] - t.boite[1]) * echelle, 1);
      portee.position.set(((t.boite[0] + t.boite[2]) / 2 - cx) * echelle, ((t.boite[1] + t.boite[3]) / 2 - cy) * echelle, Z1 + EPAISSEUR / 2 + 0.0003);
      portee.visible = true;
    } catch (erreur) {
      ombreFond.visible = false;
      portee.visible = false;
    }
    ombreFond.material.needsUpdate = true;
    portee.material.needsUpdate = true;
    if (ancienneFond) ancienneFond.dispose();
    if (anciennePortee) anciennePortee.dispose();
  }

  // --- Définition de la toile : DPR fixe, plafond de pixels ; le tampon n'est réalloué qu'au calme ---
  const dprCible = () => {
    const brut = Math.min(window.devicePixelRatio || 1, 2, etat.plafondDpr);
    const plafond = Math.sqrt(PIXELS_MAX / Math.max(1, etat.largeur * etat.hauteur));
    return Math.max(1, Math.min(qualite === 'basse' ? 1 : brut, plafond));
  };

  /** Seule allocation du tampon : à la taille finale. Renvoie vrai si le tampon a changé. */
  function allouer() {
    if (!etat.largeur || !etat.hauteur) return false;
    const d = dprCible();
    if (Math.abs(d - etat.dpr) < 1e-3 && toile.width === Math.floor(etat.largeur * d) && toile.height === Math.floor(etat.hauteur * d)) return false;
    etat.dpr = d;
    renderer.setPixelRatio(d);
    renderer.setSize(etat.largeur, etat.hauteur, false);
    etat.reallocations += 1;
    return true;
  }

  let allocationPrevue = false;
  /** Pendant un geste, la toile garde son tampon (étiré le temps du geste) : réallocation au calme seulement. */
  function planifierAllocation() {
    if (allocationPrevue) return;
    allocationPrevue = true;
    souffler().then(() => {
      allocationPrevue = false;
      if (!etat.detruit && allouer()) {
        etat.forcer = true;
        demander();
      }
    });
  }

  let baissePrevue = false;
  /** Qualité automatique : moins de pixels si les images restent lentes, appliqué après 600 ms de calme seulement. */
  function planifierBaisse() {
    if (baissePrevue) return;
    baissePrevue = true;
    souffler(CALME_QUALITE_MS).then(() => {
      baissePrevue = false;
      if (etat.detruit) return;
      etat.plafondDpr = Math.max(1, Math.min(etat.plafondDpr, etat.dpr) - 0.25);
      etat.moyenne = 0.016;
      etat.lentes = 0;
      if (allouer()) {
        etat.forcer = true;
        demander();
      }
    });
  }

  // --- cotes (surcouche SVG en pixels CSS), mises à jour seulement quand une valeur change ---
  const cotesPrec = new Float64Array(12).fill(NaN);
  let cotesChange = false;
  const memo = (i, v) => {
    if (cotesPrec[i] !== v) {
      cotesPrec[i] = v;
      cotesChange = true;
    }
  };
  const opacite = (noeud, i, v) => {
    if (cotesPrec[i] !== v) {
      cotesPrec[i] = v;
      noeud.style.opacity = String(v);
    }
  };
  function majCotes() {
    if (sansCotes) {
      if (calque.svg.style.display !== 'none') calque.svg.style.display = 'none';
      return;
    }
    const el = calque.el;
    // mouvement réduit : vue de face fixe, cotes toujours visibles et entièrement tracées
    const fixe = reduit();
    if (!fixe && (!etat.cotes || etat.trace <= 0)) {
      if (calque.svg.style.display !== 'none') calque.svg.style.display = 'none';
      return;
    }
    if (calque.svg.style.display === 'none') calque.svg.style.display = '';
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    const r = etat.rects;
    for (let n = 0; n < r.length; n += 5) {
      for (let k = 0; k < 4; k += 1) {
        vecteur.set(k & 1 ? r[n + 2] : r[n], k & 2 ? r[n + 3] : r[n + 1], r[n + 4]).project(camera);
        const px = ((vecteur.x + 1) / 2) * etat.largeur;
        const py = ((1 - vecteur.y) / 2) * etat.hauteur;
        if (px < x0) x0 = px;
        if (px > x1) x1 = px;
        if (py < y0) y0 = py;
        if (py > y1) y1 = py;
      }
    }
    const t = fixe ? 1 : etat.trace;
    const tv = POSE(clamp(t / 0.42, 0, 1));
    const th = POSE(clamp((t - 0.42) / 0.42, 0, 1));
    const tval = clamp((t - 0.8) / 0.2, 0, 1);
    const xv = Math.round(x0 - 24) + 0.5; // filets de 1 px nets
    const yt = Math.round(y0) + 0.5;
    const yb = Math.round(y1) + 0.5;
    const yh = Math.round(y1 + 24) + 0.5;
    const xa = Math.round(x0) + 0.5;
    const xb = Math.round(x1) + 0.5;
    cotesChange = false;
    memo(0, xv);
    memo(1, yt);
    memo(2, yb);
    memo(3, yh);
    memo(4, xa);
    memo(5, xb);
    memo(6, tv);
    memo(7, th);
    if (cotesChange) {
      poserLigne(el.ligneV, xv, yt, xv, yt + (yb - yt) * tv);
      poserLigne(el.traitV1, xv - 4.5, yt, xv + 4.5, yt);
      poserLigne(el.traitV2, xv - 4.5, yb, xv + 4.5, yb);
      poserLigne(el.ligneH, xa, yh, xa + (xb - xa) * th, yh);
      poserLigne(el.traitH1, xa, yh - 4.5, xa, yh + 4.5);
      poserLigne(el.traitH2, xb, yh - 4.5, xb, yh + 4.5);
      el.valeurV.setAttribute('x', String(xv - 10));
      el.valeurV.setAttribute('y', String(Math.round((yt + yb) / 2 + 4.5)));
      el.valeurH.setAttribute('x', String(Math.round((xa + xb) / 2)));
      el.valeurH.setAttribute('y', String(Math.round(yh + 22)));
    }
    opacite(el.traitV1, 8, tv > 0 ? 1 : 0);
    opacite(el.traitV2, 9, tv >= 1 ? 1 : 0);
    opacite(el.traitH1, 10, th > 0 ? 1 : 0);
    opacite(el.traitH2, 11, th >= 1 ? 1 : 0);
    if (el.valeurV.style.opacity !== String(tval)) {
      el.valeurV.style.opacity = String(tval);
      el.valeurH.style.opacity = String(tval);
    }
  }

  // --- boucle de rendu à la demande ---
  let raf = 0;
  let derniereImage = -1; // horodatage de l'image déjà rendue
  let dernierT = 0;
  let microPrevu = false;
  const actif = () => etat.pret && !etat.detruit && !etat.pauseManuelle && etat.visible && !document.hidden && !etat.perdu && etat.largeur > 0 && etat.hauteur > 0;
  const horloge = () => {
    const t = document.timeline && document.timeline.currentTime;
    return typeof t === 'number' ? t : performance.now();
  };

  function demander() {
    if (!raf && actif()) raf = window.requestAnimationFrame(image);
  }

  function arreter() {
    if (raf) window.cancelAnimationFrame(raf);
    raf = 0;
    dernierT = 0;
  }

  function animer(t, dt) {
    let encore = false;
    const fixe = reduit();
    if (fixe) {
      etat.pVue = 1;
      etat.lisser = false;
    } else if (etat.lisser) {
      etat.pVue += (etat.p - etat.pVue) * (1 - Math.exp(-dt / 0.12));
      if (Math.abs(etat.p - etat.pVue) < 1e-4) {
        etat.pVue = etat.p;
        etat.lisser = false;
      } else encore = true;
    } else etat.pVue = etat.p;
    // balancement lent et inclinaison au pointeur, seulement près de p = 0
    const calme = fixe ? 0 : 1 - smoothstep(etat.pVue, 0, 0.06);
    if (calme > 0.001) {
      etat.temps += dt;
      etat.balL = BALANCEMENT.lacet * Math.sin((etat.temps * 2 * Math.PI) / BALANCEMENT.periodeL);
      etat.balT = BALANCEMENT.tangage * Math.sin((etat.temps * 2 * Math.PI) / BALANCEMENT.periodeT + 1.3);
      encore = true;
    }
    const amorti = 1 - Math.exp(-dt / 0.25);
    etat.inclL += (etat.cibleL - etat.inclL) * amorti;
    etat.inclT += (etat.cibleT - etat.inclT) * amorti;
    if (Math.abs(etat.cibleL - etat.inclL) + Math.abs(etat.cibleT - etat.inclT) > 1e-4) encore = true;
    // rotation à la main : hors du repos et de la vue de face (travelling, gros plan), elle s'efface
    if (poidsTour(etat.pVue) < 0.001) {
      etat.tourL = 0;
      etat.tourT = 0;
      etat.vitL = 0;
      etat.retour = false;
    } else if (etat.glisse === null) {
      if (etat.retour) {
        const k = fixe ? 1 : 1 - Math.exp(-dt / ROTATION.retour);
        etat.tourL -= etat.tourL * k;
        etat.tourT -= etat.tourT * k;
        if (Math.abs(etat.tourL) + Math.abs(etat.tourT) < 1e-3) {
          etat.tourL = 0;
          etat.tourT = 0;
          etat.retour = false;
        } else encore = true;
      } else if (!fixe && Math.abs(etat.vitL) > 0.02) {
        etat.tourL += etat.vitL * dt;
        etat.vitL *= Math.exp(-dt / ROTATION.amorti);
        encore = true;
      } else etat.vitL = 0;
    }
    // pose de la seconde lettre, puis tracé des cotes visibles
    if (etat.poseDebut !== null) {
      etat.poseAvancement = clamp((t - etat.poseDebut) / DUREE_POSE, 0, 1);
      if (etat.poseAvancement >= 1) {
        etat.poseDebut = null;
        if (etat.cotes && !etat.traceManuel) etat.traceDebut = t;
      } else encore = true;
    }
    if (etat.traceDebut !== null) {
      etat.trace = clamp((t - etat.traceDebut) / DUREE_TRACE, 0, 1);
      if (etat.trace >= 1) etat.traceDebut = null;
      else encore = true;
    }
    if (etat.finition) {
      const f = etat.finition;
      const e = POSE(clamp((t - f.debut) / DUREE_FINITION, 0, 1));
      matiere.color.lerpColors(f.de, f.vers, e);
      matiere.roughness = lerp(f.deR, f.versR, e);
      matiereChaine.color.copy(matiere.color);
      matiereChaine.roughness = matiere.roughness;
      if (e >= 1) etat.finition = null;
      else encore = true;
    }
    // qualité automatique : seulement si les images restent lentes pendant une animation continue,
    // et la baisse attend le calme (jamais de réallocation pendant ou à la fin d'un geste)
    if (qualite === 'auto' && encore && dt > 0) {
      etat.moyenne = etat.moyenne * 0.94 + dt * 0.06;
      etat.lentes = etat.moyenne > LENT_S ? etat.lentes + 1 : 0;
      if (etat.lentes > LENT_IMAGES && etat.plafondDpr > 1 && etat.dpr > 1) planifierBaisse();
    }
    etat.encore = encore;
    return encore;
  }

  function rendre() {
    if (etat.clesSales) recalculerCles();
    if (reduit()) etat.pVue = 1;
    const pv = etat.pVue;
    evaluerVue(pv);
    const calme = reduit() ? 0 : 1 - smoothstep(pv, 0, 0.06);
    const pt = poidsTour(pv);
    poserCamera(camera, vue.lacet + calme * (etat.balL + etat.inclL) + pt * etat.tourL, vue.tangage + calme * (etat.balT + etat.inclT) + pt * etat.tourT, vue.sx, vue.sy, vue.s);
    // l'environnement suit le centre du pendant à l'écran dans les vues cadrées (lecture des reflets indépendante de
    // la mise en page et des lettres) ; en macro, l'objet remplit l'écran : le cône de reflets est celui de l'écran
    const focale = etat.hauteur / 2 / Math.tan(degToRad(FOV / 2));
    vecteur.copy(pointCentre).project(camera);
    const ox = lerp(etat.largeur / 2, ((vecteur.x + 1) / 2) * etat.largeur, vue.wc);
    const oy = lerp(etat.hauteur / 2, ((1 - vecteur.y) / 2) * etat.hauteur, vue.wc);
    scene.environmentRotation.set(Math.atan((oy - etat.hauteur / 2) / focale), Math.atan((ox - etat.largeur / 2) / focale) + vue.env, 0);
    lettre2.position.copy(base2);
    if (etat.poseAvancement < 1) lettre2.position.addScaledVector(droite, (GLISSEMENT_PX / vue.s) * (1 - POSE(etat.poseAvancement)));
    portee.material.opacity = OPACITE_PORTEE * POSE(etat.poseAvancement);
    // plafond de la chaîne au repos : la ligne de coupe sort par le haut pendant le travelling
    if (etat.plafond !== null && !reduit()) {
      const poids = 1 - smoothstep(pv, 0.06, 0.16);
      const y = lerp(-64, etat.plafond, poids);
      plafondChaine.value = poids > 0.001 ? (etat.hauteur - y) * etat.dpr : 1e9;
    } else plafondChaine.value = 1e9;
    const debut = etat.images ? 0 : performance.now();
    renderer.render(scene, camera);
    etat.images += 1;
    if (etat.images === 1) {
      etat.premiereImage = { ms: Math.round((performance.now() - debut) * 10) / 10, t: Math.round(performance.now()), toile: [toile.width, toile.height] };
      marquer('premiere-image');
    }
    majCotes();
    infoImage.progression = pv;
    infoImage.macro = 1 - vue.wc;
    infoImage.largeur = etat.largeur;
    infoImage.hauteur = etat.hauteur;
    for (let i = 0; i < abonnes.length; i += 1) {
      try {
        abonnes[i](infoImage);
      } catch (erreur) {
        // un abonné en erreur ne casse pas le rendu
      }
    }
    if (etat.images === 1) conteneur.dispatchEvent(new CustomEvent('mq:chiffre-premiere-image', { bubbles: true }));
  }

  /** Une avance du temps par image du navigateur ; un nouveau rendu dans la même image si l'état a changé. */
  function cycle(t, forcer) {
    if (t !== derniereImage) {
      const dt = dernierT ? Math.min((t - dernierT) / 1000, 0.05) : 0;
      dernierT = t;
      derniereImage = t;
      animer(t, dt);
      rendre();
    } else if (forcer) rendre();
    return etat.encore;
  }

  function image(t) {
    raf = 0;
    if (!actif()) return;
    const forcer = etat.forcer;
    etat.forcer = false;
    if (cycle(t, forcer)) demander();
    else dernierT = 0;
  }

  function rendreMicro() {
    microPrevu = false;
    if (!actif()) return;
    etat.forcer = false;
    if (cycle(horloge(), true)) demander();
  }

  /** Rendu dans la même image que l'appel (défilement) : aucune image de retard sur les textes. */
  function rendreBientot() {
    if (microPrevu || !actif()) return;
    microPrevu = true;
    window.queueMicrotask(rendreMicro);
  }

  // --- taille, visibilité, pointeur, mouvement réduit ---
  function redimensionner() {
    const W = enveloppe.clientWidth;
    const H = enveloppe.clientHeight;
    if (W === etat.largeur && H === etat.hauteur) return;
    etat.largeur = W;
    etat.hauteur = H;
    if (!W || !H) return;
    camera.aspect = W / H;
    camera.updateProjectionMatrix();
    calque.svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
    cotesPrec.fill(NaN);
    etat.clesSales = true;
    // avant la première image ou au calme : tampon à la taille finale tout de suite ; pendant un geste : au calme
    if (!etat.pret || calmeDepuis() >= CALME_MS) allouer();
    else planifierAllocation();
    planifierMacro();
    etat.forcer = true;
    demander();
  }

  /** Poids de la rotation à la main : 1 au repos et de face, 0 pendant le travelling et le gros plan. */
  function poidsTour(pv) {
    if (!rotation) return 0;
    if (reduit()) return 1;
    return Math.max(1 - smoothstep(pv, 0.02, 0.1), smoothstep(pv, 0.8, 0.88));
  }

  /** Le pointeur est-il sur le pendant (disque autour de son centre projeté) ? */
  function surPendant(evenement) {
    if (!etat.images || !etat.largeur) return false;
    const r = toile.getBoundingClientRect();
    if (r.width < 1 || evenement.clientX < r.left || evenement.clientX > r.right || evenement.clientY < r.top || evenement.clientY > r.bottom) return false;
    const v = new THREE.Vector3().copy(pointCentre).project(camera);
    const x = r.left + ((v.x + 1) / 2) * r.width;
    const y = r.top + ((1 - v.y) / 2) * r.height;
    return Math.hypot(evenement.clientX - x, evenement.clientY - y) <= vue.s * ROTATION.rayon * (r.width / etat.largeur);
  }

  const glisser = { x: 0, y: 0, t: 0, bouge: false };
  let pastille = null;
  function finAstuce() {
    if (!pastille) return;
    const el = pastille;
    pastille = null;
    el.style.opacity = '0';
    window.setTimeout(() => el.remove(), 400);
    try {
      window.sessionStorage.setItem('mq-rotation-vue', '1');
    } catch (e) {
      // stockage indisponible : l'astuce reviendra, sans gravité
    }
  }

  function surAppui(evenement) {
    if (etat.glisse !== null || evenement.button > 0 || !actif() || poidsTour(etat.pVue) < 0.5) return;
    const cible = evenement.target;
    if (cible && cible.closest && cible.closest('a, button, input, label, select, textarea, summary, [role="button"], [role="radio"]')) return;
    if (window.getComputedStyle(conteneur).opacity === '0' || window.getComputedStyle(conteneur).visibility === 'hidden') return;
    if (!surPendant(evenement)) return;
    etat.glisse = evenement.pointerId;
    etat.vitL = 0;
    etat.retour = false;
    glisser.x = evenement.clientX;
    glisser.y = evenement.clientY;
    glisser.t = performance.now();
    glisser.bouge = false;
    document.documentElement.style.userSelect = 'none';
    document.documentElement.style.webkitUserSelect = 'none';
    if (evenement.pointerType === 'mouse') document.documentElement.style.cursor = 'grabbing';
  }

  function surGlisse(evenement) {
    if (evenement.pointerId !== etat.glisse) return;
    const dx = evenement.clientX - glisser.x;
    const dy = evenement.clientY - glisser.y;
    const t = performance.now();
    const dt = Math.max(1, t - glisser.t) / 1000;
    glisser.x = evenement.clientX;
    glisser.y = evenement.clientY;
    glisser.t = t;
    etat.tourL -= dx * ROTATION.parPx;
    if (evenement.pointerType === 'mouse') etat.tourT = clamp(etat.tourT + dy * ROTATION.tangageParPx, -ROTATION.tangage, ROTATION.tangage);
    etat.vitL = lerp(etat.vitL, clamp((-dx * ROTATION.parPx) / dt, -6, 6), 0.5);
    if (!glisser.bouge && Math.abs(dx) + Math.abs(dy) > 2) {
      glisser.bouge = true;
      finAstuce();
      conteneur.dispatchEvent(new CustomEvent('mq:chiffre-rotation', { bubbles: true }));
    }
    etat.forcer = true;
    demander();
  }

  function surLacher(evenement) {
    if (evenement.pointerId !== etat.glisse) return;
    etat.glisse = null;
    if (performance.now() - glisser.t > 90 || reduit()) etat.vitL = 0;
    document.documentElement.style.userSelect = '';
    document.documentElement.style.webkitUserSelect = '';
    document.documentElement.style.cursor = '';
    demander();
  }

  /** Double-clic ou double-tap sur le pendant : retour de face par le plus court chemin. */
  function surDouble(evenement) {
    if (!actif() || !surPendant(evenement)) return;
    etat.tourL = Math.atan2(Math.sin(etat.tourL), Math.cos(etat.tourL));
    etat.vitL = 0;
    etat.retour = true;
    demander();
  }

  if (rotation) {
    // le doigt glisse sur le côté pour tourner ; le défilement vertical reste au navigateur
    conteneur.style.touchAction = 'pan-y';
    window.addEventListener('pointerdown', surAppui, { passive: true });
    window.addEventListener('pointermove', surGlisse, { passive: true });
    window.addEventListener('pointerup', surLacher, { passive: true });
    window.addEventListener('pointercancel', surLacher, { passive: true });
    window.addEventListener('dblclick', surDouble);
    let dejaVue = false;
    try {
      dejaVue = window.sessionStorage.getItem('mq-rotation-vue') === '1';
    } catch (e) {
      dejaVue = false;
    }
    if (astuce && !plein && !dejaVue) {
      pastille = document.createElement('p');
      pastille.className = 'mq-chiffre-3d__astuce';
      pastille.textContent = `↔ ${astuce}`;
      pastille.style.cssText = 'position:absolute;left:50%;bottom:12px;transform:translateX(-50%);margin:0;padding:4px 10px;white-space:nowrap;font:400 12px/1.4 var(--font-mesure, monospace);color:var(--graphite, #575c66);background:color-mix(in srgb, var(--pierre, #e6e5e1) 85%, transparent);opacity:1;transition:opacity 300ms';
      enveloppe.append(pastille);
    }
  }

  function surPointeur(evenement) {
    const r = enveloppe.getBoundingClientRect();
    const nx = (evenement.clientX - (r.left + r.width / 2)) / Math.max(1, window.innerWidth / 2);
    const ny = (evenement.clientY - (r.top + r.height / 2)) / Math.max(1, window.innerHeight / 2);
    etat.cibleL = clamp(nx, -1, 1) * POINTEUR.lacet;
    etat.cibleT = clamp(ny, -1, 1) * POINTEUR.tangage;
    if (etat.pVue < 0.06) demander();
  }

  function surSortie() {
    etat.cibleL = 0;
    etat.cibleT = 0;
    demander();
  }

  let ecoutePointeur = false;
  function majPointeur() {
    const voulu = interactif && !reduit() && requeteFin.matches;
    if (voulu === ecoutePointeur) return;
    ecoutePointeur = voulu;
    if (voulu) {
      window.addEventListener('pointermove', surPointeur, { passive: true });
      document.documentElement.addEventListener('pointerleave', surSortie);
    } else {
      window.removeEventListener('pointermove', surPointeur);
      document.documentElement.removeEventListener('pointerleave', surSortie);
      surSortie();
    }
  }

  function surMouvement() {
    if (reduit()) {
      etat.poseDebut = null;
      etat.poseAvancement = 1;
      etat.traceDebut = null;
      etat.trace = 1;
      etat.cotes = true;
    }
    majPointeur();
    etat.forcer = true;
    demander();
  }

  function surOnglet() {
    if (document.hidden) arreter();
    else {
      etat.forcer = true;
      demander();
    }
  }

  function surPerte(evenement) {
    evenement.preventDefault();
    etat.perdu = true;
    arreter();
  }

  function surRetour() {
    etat.perdu = false;
    studio.dispose();
    studio = construireStudio(renderer, taillePMREM, definitionStudio);
    scene.environment = studio.texture;
    if (scene.background) scene.background = studio.texture;
    etat.forcer = true;
    demander();
  }

  /**
   * Chauffe : tampon alloué à sa taille finale, puis un rendu complet de la vue de départ, toile masquée, aussitôt
   * effacé. Programmes, tampons, textures et premier tracé sont payés ici, au calme : la première image visible
   * ne paie ni l'allocation ni le premier tracé.
   */
  function chauffer(pixel) {
    if (!etat.largeur || !etat.hauteur) return;
    recalculerCles();
    evaluerVue(etat.pVue);
    poserCamera(camera, vue.lacet, vue.tangage, vue.sx, vue.sy, vue.s);
    const visibilite = toile.style.visibility;
    toile.style.visibility = 'hidden';
    if (pixel) {
      // un seul pixel : le pilote alloue et valide le tampon multiéchantillonné sans tracer toute la toile
      renderer.setScissorTest(true);
      renderer.setScissor(0, 0, 1, 1);
    }
    renderer.render(scene, camera);
    if (pixel) renderer.setScissorTest(false);
    renderer.clear();
    toile.style.visibility = visibilite;
  }

  // --- préparation : une étape par temps libre, au calme ---
  const a = normaliser(i1);
  const b = normaliser(i2);
  const l1 = glyphes.lettres[a] ? a : 'A';
  const l2 = glyphes.lettres[b] ? b : 'L';
  await souffler();
  ouvrirEtape();
  geometrie(l1);
  fermerEtape('lettre-1');
  await souffler();
  ouvrirEtape();
  geometrie(l2);
  fermerEtape('lettre-2');
  await souffler();
  ouvrirEtape();
  const analyseInitiale = analyserChiffre(disposer(glyphes, l1, l2), glyphes);
  fermerEtape('analyse');
  await souffler();
  ouvrirEtape();
  appliquerLettres(l1, l2, { ombres: false, analyse: analyseInitiale });
  fermerEtape('disposition');
  await souffler();
  ouvrirEtape();
  poserOmbres();
  fermerEtape('ombres');

  // programmes compilés avant le premier affichage (en parallèle si KHR_parallel_shader_compile)
  await souffler();
  ouvrirEtape();
  scene.updateMatrixWorld(true);
  let compilation = null;
  try {
    compilation = renderer.compileAsync(scene, camera);
  } catch (erreur) {
    compilation = null;
  }
  fermerEtape('compilation');
  try {
    if (compilation) await compilation;
  } catch (erreur) {
    // la compilation se fera au premier rendu
  }
  const programmesCompiles = renderer.info.programs ? renderer.info.programs.length : -1;
  await souffler();
  ouvrirEtape();
  for (const o of [ombreFond, portee]) if (o.material.map) renderer.initTexture(o.material.map);
  fermerEtape('textures');
  // pipelines du pilote pour les formats de la toile (multiéchantillonnage, sRGB), créés au premier tracé : on les
  // paie sur un tampon de 16 px, où le premier tracé est court, avant d'allouer la toile à sa taille finale
  await souffler();
  ouvrirEtape();
  renderer.setPixelRatio(1);
  renderer.setSize(16, 16, false);
  etat.largeur = 16;
  etat.hauteur = 16;
  camera.aspect = 1;
  camera.updateProjectionMatrix();
  recalculerCles();
  evaluerVue(etat.pVue);
  poserCamera(camera, vue.lacet, vue.tangage, vue.sx, vue.sy, vue.s);
  renderer.render(scene, camera);
  renderer.clear();
  renderer.getContext().finish();
  etat.largeur = 0;
  etat.hauteur = 0;
  etat.dpr = 0;
  fermerEtape('pipelines');
  await souffler();
  ouvrirEtape();
  redimensionner();
  allouer();
  fermerEtape('allocation');
  await souffler();
  ouvrirEtape();
  chauffer(true);
  fermerEtape('premier-trace');
  await souffler();
  ouvrirEtape();
  chauffer(false);
  fermerEtape('chauffe');
  const observateurTaille = new ResizeObserver(redimensionner);
  observateurTaille.observe(enveloppe);
  const observateurVue = new IntersectionObserver((entrees) => {
    etat.visible = entrees[entrees.length - 1].isIntersecting;
    if (etat.visible) {
      etat.forcer = true;
      demander();
    } else arreter();
  }, { rootMargin: '64px' });
  observateurVue.observe(enveloppe);
  document.addEventListener('visibilitychange', surOnglet);
  requeteReduit.addEventListener('change', surMouvement);
  requeteFin.addEventListener('change', majPointeur);
  toile.addEventListener('webglcontextlost', surPerte);
  toile.addEventListener('webglcontextrestored', surRetour);
  majPointeur();
  etat.pret = true;
  redimensionner();
  demander(); // première image à la prochaine image du navigateur : l'appelant a le temps de poser son cadre

  // lecture d'un rectangle sans allocation (setCadre et setCadreFace sont appelés à chaque image)
  const rectLu = { x: 0, y: 0, w: 0, h: 0 };
  const lireRect = (zone, sortie = rectLu) => {
    if (!zone) return null;
    const x = Number(zone.x);
    const y = Number(zone.y);
    const w = Number(zone.w);
    const h = Number(zone.h);
    if (!(Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(w) && Number.isFinite(h)) || w <= 0 || h <= 0) return null;
    sortie.x = x;
    sortie.y = y;
    sortie.w = w;
    sortie.h = h;
    return sortie;
  };
  const memeRect = (c, x, y, w, h) => c && c.x === x && c.y === y && c.w === w && c.h === h;

  const api = {
    /** Change les lettres ; avec animer, la seconde lettre se pose (8 px, 300 ms) puis les cotes visibles se retracent. */
    setLettres(m1, m2, { animer: anime = true } = {}) {
      if (etat.detruit) return false;
      const x = normaliser(m1);
      const y = normaliser(m2);
      if (!glyphes.lettres[x] || !glyphes.lettres[y]) return false;
      if (x !== etat.lettres[0] || y !== etat.lettres[1]) appliquerLettres(x, y);
      if (anime && !reduit()) {
        etat.poseDebut = performance.now();
        etat.poseAvancement = 0;
        etat.traceDebut = null;
        if (etat.cotes && !etat.traceManuel) etat.trace = 0;
      } else {
        etat.poseDebut = null;
        etat.poseAvancement = 1;
        if (!etat.traceManuel) etat.trace = 1;
        etat.traceDebut = null;
      }
      etat.forcer = true;
      demander();
      return true;
    },
    /** 'doree' ou 'argentee' (fondu de 300 ms). */
    setFinition(f) {
      if (etat.detruit || !FINITIONS[f]) return;
      const vers = new THREE.Color().setRGB(...FINITIONS[f].f0, THREE.LinearSRGBColorSpace);
      if (reduit() || !etat.images) {
        matiere.color.copy(vers);
        matiere.roughness = FINITIONS[f].rugosite;
        matiereChaine.color.copy(vers);
        matiereChaine.roughness = FINITIONS[f].rugosite;
        etat.finition = null;
      } else {
        etat.finition = { de: matiere.color.clone(), vers, deR: matiere.roughness, versR: FINITIONS[f].rugosite, debut: performance.now() };
      }
      etat.forcer = true;
      demander();
    },
    /** p de 0 à 1, appliqué tel quel (immediat) ; le lissage interne ne sert que si immediat vaut false. */
    setProgression(p, { immediat = true } = {}) {
      if (etat.detruit) return;
      const v = clamp(Number(p) || 0, 0, 1);
      const change = v !== etat.p;
      etat.p = v;
      if (reduit()) {
        etat.lisser = false;
        etat.pVue = 1; // mouvement réduit : vue de face fixe
      } else if (immediat || !etat.images) {
        etat.lisser = false;
        etat.pVue = v;
      } else etat.lisser = true;
      if (!change && !etat.lisser) return;
      noterGeste(); // p suit le défilement : aucun calcul ni aucune préparation tant qu'il bouge
      if (immediat) rendreBientot();
      else demander();
    },
    /** Cadre de repos de l'objet (px CSS, relatifs au conteneur) : vue 3/4. Appelable à chaque image. */
    setCadre(zone) {
      if (etat.detruit) return;
      const r = lireRect(zone);
      if (!r) return;
      const plafond = zone.plafond === undefined || zone.plafond === null || !Number.isFinite(Number(zone.plafond)) ? null : Number(zone.plafond);
      if (plafond !== etat.plafond) {
        etat.plafond = plafond;
        etat.forcer = true;
        demander();
      }
      if (memeRect(etat.cadre, r.x, r.y, r.w, r.h)) return;
      if (etat.cadre) Object.assign(etat.cadre, r);
      else etat.cadre = { ...r };
      etat.clesSales = true;
      planifierMacro(); // le zoom se compte par rapport au repos : recalculé au calme
      etat.forcer = true;
      demander();
    },
    /** v3. Cadre de la vue de face (p ≥ 0,84), interpolé pendant le recul ; null : même cadre que setCadre. */
    setCadreFace(zone) {
      if (etat.detruit) return;
      if (zone === null || zone === undefined) {
        if (!etat.cadreFace) return;
        etat.cadreFace = null;
      } else {
        const r = lireRect(zone);
        if (!r || memeRect(etat.cadreFace, r.x, r.y, r.w, r.h)) return;
        if (etat.cadreFace) Object.assign(etat.cadreFace, r);
        else etat.cadreFace = { ...r };
      }
      etat.clesSales = true;
      etat.forcer = true;
      demander();
    },
    /**
     * v3. Candidats de macro [{ cible: { x, y }, reserve: { x, y, w, h } }, …] (px CSS dans le conteneur) ; calcul au
     * calme, puis « mq:chiffre-macro » { indice, zoom } si le choix change. null ou [] : macro par défaut.
     */
    setMacro(candidats) {
      if (etat.detruit) return;
      const liste = [];
      if (Array.isArray(candidats)) {
        for (const c of candidats) {
          const x = Number(c && c.cible && c.cible.x);
          const y = Number(c && c.cible && c.cible.y);
          if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
          const r = lireRect(c.reserve);
          liste.push({ cible: { x, y }, reserve: r && { ...r } });
        }
      }
      const cle = JSON.stringify(liste);
      if (cle === etat.cleCandidats) return;
      etat.cleCandidats = cle;
      etat.candidats = liste;
      if (!liste.length) {
        etat.macroEmis = '';
        if (etat.macro) {
          etat.macro = null;
          etat.clesSales = true;
          etat.forcer = true;
          demander();
        }
        return;
      }
      planifierMacro();
    },
    /** Affiche ou masque les cotes ; à l'affichage, elles se tracent en 240 ms. */
    afficherCotes(visible) {
      if (etat.detruit) return;
      const v = Boolean(visible);
      if (v && (!etat.cotes || etat.traceManuel)) {
        etat.trace = reduit() ? 1 : 0;
        etat.traceDebut = reduit() || etat.poseDebut !== null ? null : performance.now();
      }
      etat.traceManuel = false;
      etat.cotes = v;
      etat.forcer = true;
      demander();
    },
    /** Tracé partiel des cotes, de 0 à 1 : verticale, puis horizontale, puis valeurs en fondu. */
    tracerCotes(t) {
      if (etat.detruit) return;
      const v = clamp(Number(t) || 0, 0, 1);
      etat.traceManuel = true;
      etat.traceDebut = null;
      etat.cotes = v > 0;
      etat.trace = v;
      rendreBientot();
    },
    /** Position d'un point du chiffre en px CSS dans le conteneur ({ x, y }), ou null. */
    pointEcran(nom = 'croisement') {
      if (etat.detruit || !etat.images || !etat.largeur) return null;
      if (nom === 'haut') vecteur.copy(pointHaut);
      else if (nom === 'centre') vecteur.copy(pointCentre);
      else if (nom === 'croisement') vecteur.copy(croisement);
      else return null;
      vecteur.project(camera);
      if (!Number.isFinite(vecteur.x) || vecteur.z < -1 || vecteur.z > 1) return null;
      return { x: ((vecteur.x + 1) / 2) * etat.largeur, y: ((1 - vecteur.y) / 2) * etat.hauteur };
    },
    /** cb({ progression, macro, largeur, hauteur }) après chaque rendu (objet réutilisé) ; renvoie le désabonnement. */
    surImage(cb) {
      if (typeof cb !== 'function') return () => {};
      abonnes.push(cb);
      return () => {
        const i = abonnes.indexOf(cb);
        if (i >= 0) abonnes.splice(i, 1);
      };
    },
    pause() {
      etat.pauseManuelle = true;
      arreter();
    },
    reprendre() {
      etat.pauseManuelle = false;
      etat.forcer = true;
      demander();
    },
    /** Libère tout : boucle, écouteurs, géométries, matières, textures, environnement, contexte, DOM. */
    detruire() {
      if (etat.detruit) return;
      etat.detruit = true;
      arreter();
      abonnes.length = 0;
      observateurTaille.disconnect();
      observateurVue.disconnect();
      document.removeEventListener('visibilitychange', surOnglet);
      requeteReduit.removeEventListener('change', surMouvement);
      requeteFin.removeEventListener('change', majPointeur);
      window.removeEventListener('pointermove', surPointeur);
      document.documentElement.removeEventListener('pointerleave', surSortie);
      window.removeEventListener('pointerdown', surAppui);
      window.removeEventListener('pointermove', surGlisse);
      window.removeEventListener('pointerup', surLacher);
      window.removeEventListener('pointercancel', surLacher);
      window.removeEventListener('dblclick', surDouble);
      toile.removeEventListener('webglcontextlost', surPerte);
      toile.removeEventListener('webglcontextrestored', surRetour);
      for (const g of geometries.values()) g.dispose();
      geometries.clear();
      VIDE.dispose();
      if (beliere) beliere.geometry.dispose();
      if (maillon) maillon.dispose();
      if (liens) liens.dispose();
      matiere.dispose();
      matiereChaine.dispose();
      plan.dispose();
      for (const o of [ombreFond, portee]) {
        if (o.material.map) o.material.map.dispose();
        o.material.dispose();
      }
      studio.dispose();
      scene.environment = null;
      scene.background = null;
      renderer.dispose();
      renderer.forceContextLoss();
      enveloppe.remove();
      if (conteneur.mqChiffre3D && conteneur.mqChiffre3D.api === api) delete conteneur.mqChiffre3D;
    },
  };

  // --- contrôle (docs/qa/v3-B) : état, coût des étapes, zone de mesure des reflets, masque de silhouette ---
  const ecranDe = (x, y, z) => {
    vecteur.set(x, y, z).project(camera);
    return { x: ((vecteur.x + 1) / 2) * etat.largeur, y: ((1 - vecteur.y) / 2) * etat.hauteur };
  };
  const qa = {
    etapes,
    etat() {
      return {
        dpr: etat.dpr,
        toile: [toile.width, toile.height],
        css: [etat.largeur, etat.hauteur],
        reallocations: etat.reallocations,
        plafondDpr: etat.plafondDpr,
        images: etat.images,
        premiereImage: etat.premiereImage,
        programmes: [programmesCompiles, renderer.info.programs ? renderer.info.programs.length : -1],
        p: etat.p,
        lettres: etat.lettres.join(''),
        plein,
        cadre: etat.cadre && { ...etat.cadre },
        cadreFace: etat.cadreFace && { ...etat.cadreFace },
        repos: { ...cleRepos },
        face: { ...cleFace },
        vue: { ...vue },
        macro: etat.macro && { ...etat.macro, resultats: etat.macro.resultats.slice() },
        candidats: etat.candidats && etat.candidats.map((c) => ({ cible: { ...c.cible }, reserve: c.reserve && { ...c.reserve } })),
        plafond: etat.plafond,
        croisement: croisement.toArray(),
      };
    },
    /** Zone de mesure des reflets : milieu du fût de la lettre n (15 % à 85 % de sa hauteur), en px CSS. */
    zoneFut(n = 1) {
      const f = etat.futs[n];
      if (!f || !etat.images) return null;
      const o = n === 0 ? lettre1.position : lettre2.position;
      const dx = o.x - (n === 0 ? lettre1.position.x : base2.x);
      const hautP = ecranDe(f.x + dx, lerp(f.bas, f.haut, 0.85), f.z);
      const basP = ecranDe(f.x + dx, lerp(f.bas, f.haut, 0.15), f.z);
      const g = ecranDe(f.x + dx - f.demiLargeur, (f.bas + f.haut) / 2, f.z);
      const d = ecranDe(f.x + dx + f.demiLargeur, (f.bas + f.haut) / 2, f.z);
      return { x: (hautP.x + basP.x) / 2, haut: hautP.y, bas: basP.y, xHaut: hautP.x, xBas: basP.x, largeur: Math.abs(d.x - g.x) };
    },
    /** Pixels de silhouette (pendant, bélière, chaîne) dans `rect` (px CSS) pour la vue p, par un rendu masqué. */
    masque(rect, p) {
      const W = etat.largeur;
      const H = etat.hauteur;
      if (!W || !H) return null;
      const cibleRendu = new THREE.WebGLRenderTarget(W, H);
      const blanc = new THREE.MeshBasicMaterial({ color: 0xffffff });
      const p0 = etat.pVue;
      if (typeof p === 'number') etat.pVue = clamp(p, 0, 1);
      if (etat.clesSales) recalculerCles();
      evaluerVue(etat.pVue);
      const calme = 1 - smoothstep(etat.pVue, 0, 0.06);
      poserCamera(camera, vue.lacet + calme * (etat.balL + etat.inclL), vue.tangage + calme * (etat.balT + etat.inclT), vue.sx, vue.sy, vue.s);
      const vis = [ombreFond.visible, portee.visible];
      ombreFond.visible = false;
      portee.visible = false;
      scene.overrideMaterial = blanc;
      renderer.setRenderTarget(cibleRendu);
      renderer.clear();
      renderer.render(scene, camera);
      const px = new Uint8Array(W * H * 4);
      renderer.readRenderTargetPixels(cibleRendu, 0, 0, W, H, px);
      renderer.setRenderTarget(null);
      scene.overrideMaterial = null;
      [ombreFond.visible, portee.visible] = vis;
      etat.pVue = p0;
      cibleRendu.dispose();
      blanc.dispose();
      let dedans = 0;
      let total = 0;
      const x0 = Math.max(0, Math.floor(rect ? rect.x : 0));
      const y0 = Math.max(0, Math.floor(rect ? rect.y : 0));
      const x1 = Math.min(W, Math.ceil(rect ? rect.x + rect.w : W));
      const y1 = Math.min(H, Math.ceil(rect ? rect.y + rect.h : H));
      for (let y = 0; y < H; y += 1) {
        const ligne = (H - 1 - y) * W * 4;
        for (let x = 0; x < W; x += 1) {
          if (px[ligne + x * 4 + 3] > 0) {
            total += 1;
            if (x >= x0 && x < x1 && y >= y0 && y < y1) dedans += 1;
          }
        }
      }
      etat.forcer = true;
      demander();
      return { dedans, total };
    },
    /** Calcul immédiat de setMacro (contrôle du coût), sans attendre le calme. */
    resoudre() {
      return resoudreMacro();
    },
  };
  Object.defineProperty(conteneur, 'mqChiffre3D', { value: { api, qa }, configurable: true, enumerable: false, writable: true });

  if (debogage) {
    api.studio = (visible) => {
      scene.background = visible ? studio.texture : null;
      etat.forcer = true;
      demander();
    };
    api.rugosite = (r) => {
      matiere.roughness = r;
      matiereChaine.roughness = r;
      etat.forcer = true;
      demander();
    };
    /** Réglage à chaud du studio, de la matière et de l'exposition (banc d'essai). */
    api.regler = ({ studio: def, f0, rugosite, nettete: n, exposition } = {}) => {
      if (def) {
        definitionStudio = def;
        const ancien = studio;
        studio = construireStudio(renderer, taillePMREM, definitionStudio);
        scene.environment = studio.texture;
        if (scene.background) scene.background = studio.texture;
        ancien.dispose();
      }
      if (f0) {
        matiere.color.setRGB(...f0, THREE.LinearSRGBColorSpace);
        matiereChaine.color.copy(matiere.color);
      }
      if (typeof rugosite === 'number') {
        matiere.roughness = rugosite;
        matiereChaine.roughness = rugosite;
      }
      if (typeof n === 'number') nettete.value = n;
      if (typeof exposition === 'number') renderer.toneMappingExposure = exposition;
      etat.forcer = true;
      demander();
    };
    api.etat = () => qa.etat();
    /** Objets internes pour les outils du banc d'essai (carte de l'environnement), en mode débogage seulement. */
    api.interne = () => ({ renderer, scene, camera, studio, matiere, silhouette, futs: etat.futs, etat, vue });
  }
  if (reduit()) {
    etat.cotes = true;
    etat.trace = 1;
  }
  return api;
}
